import React, { useEffect, useState, useRef } from 'react';
import { Plus, Trash2, Search, Edit2, Check, X, Package, Upload, Download, FileSpreadsheet } from 'lucide-react';
import * as XLSX from 'xlsx';
import { getSchemesForCountry, getDefaultSchemeForCountryAndRegion, getSchemeById as getSchemeByIdBase, computeTaxBreakdown, computeTaxScenarios, formatTaxBreakdown, TaxScheme, TAX_SCHEMES, buildCustomScheme, CustomTaxSchemeConfig } from '../utils/taxSchemes';
import { getOrderedCountryList } from '../utils/countryNames';
import bwipjs from 'bwip-js';
import jsPDF from 'jspdf';
import { getPermissions } from '../utils/permissions';
import { postLog } from '../utils/authApi';
import { getCurrencySymbol } from '../utils/currency';
import QuickScannerModal from './QuickScannerModal';
import StatePicker from './StatePicker';
   import { printElementUSB, isUSBPrinterConnected, connectUSBPrinter, tryAutoReconnectUSB, getUSBPrinterName, disconnectUSBPrinter } from '../utils/labelUsbPrinter';
import { connectBluetoothPrinter, getBTPrinterName } from '../utils/bluetoothPrinter';
import { printBarcodeLabelsBT } from '../utils/barcodeLabelPrinter';
import { printBarcodeLabelsZPL } from '../utils/zplLabelPrinter';
const BACKEND = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';

interface Product {
  id: number;
  barcode: string;
  name: string;
  description?: string;
  brand?: string;
  category?: string;
  rate: number;
  unit?: string;
  stock_quantity: number;
  low_stock_threshold?: number;
  custom_fields?: Record<string, string>;
  org_id?: number | null;
  outlet_id?: number | null;
  tax_rate?: number;       // B2C / standard rate (e.g. 4%)
  tax_name?: string;       // B2C tax name (e.g. "Retail VAT", "GST")
  b2b_tax_rate?: number;   // B2B business rate (e.g. 8%)
  b2b_tax_name?: string;   // B2B tax name (e.g. "Corporate VAT", "Wholesale GST")
  tax_inclusive?: boolean;
  tax_scheme_id?: string;
}

const empty = (defaultSchemeId?: string): Omit<Product, 'id'> => ({
  barcode: '', name: '', description: '', brand: '', category: '', rate: 0, unit: 'pcs', stock_quantity: 0, low_stock_threshold: 5,
  tax_rate: 0, tax_name: '', b2b_tax_rate: undefined, b2b_tax_name: '', tax_inclusive: false, tax_scheme_id: defaultSchemeId || 'custom',
});

// Master set of all countries that have sub-national tax regions, states, or exempt zones (matches mobile app)
const REGIONAL_TAX_COUNTRIES = new Set([
  'IN', 'US', 'CA', 'AU', 'BR', 'AR', 'PK', 'ES', 'PT', 'CH', 'NG',
  'MX', 'CO', 'ID', 'PE', 'MY', 'PH', 'CL', 'CN', 'RU', 'EC', 'VN',
  'DE', 'IT', 'FI', 'NO', 'DK', 'AT', 'BA', 'KR', 'TH', 'IL',
]);
const generateBarcode = () => {
  const rand = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `PRD-${Date.now().toString(36).toUpperCase()}-${rand}`;
};

const generateEAN13 = async (): Promise<string> => {
  const jwt = localStorage.getItem('cloudairambo_jwt') || '';
  const res = await fetch(`${BACKEND}/products/generate-barcode`, {
    headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
  });
  const data = await res.json();
  return data.barcode;
};

// Fuzzy field patterns. Within one field's array, patterns are ordered
// most-specific-first — when two columns in the same file both look like
// candidates for the same field (e.g. "Rate" and "MRP" both look like a
// price), the earlier-listed pattern wins regardless of column order in
// the sheet, instead of whichever column happens to come last silently
// overwriting the others.
const FIELD_PATTERNS: Record<string, RegExp[]> = {
  barcode: [/\b(bar\s?code|ean(-?13)?|upc|sku|item\s?code|product\s?code)\b/, /^code$/],
  name: [/\b(product|item)\s?name\b/, /^(name|product|item)$/, /\b(particulars|goods|description\s?of\s?goods)\b/],
  description: [/^(desc|description|details)$/],
  brand: [/\b(brand|manufacturer|make)\b/],
  category: [/\b(category|type|group)\b/],
  rate: [/\b(unit\s?price|selling\s?price|sale\s?price|unit\s?rate)\b/, /^(rate|price|amount)$/, /\bmrp\b/, /\bcost(\s?price)?\b/],
  unit: [/^(unit|uom|measure)$/],
  // b2b_tax_rate MUST come before tax_rate — otherwise a header like
  // "B2B Tax Rate (%)" gets misclassified as the plain tax_rate, since
  // classifyHeader returns on the first field (in object order) whose
  // pattern matches, and tax_rate's own /\btax\s?rate\b/ pattern also
  // matches the substring inside "b2b tax rate".
  b2b_tax_rate: [/\b(b2b\s?tax(\s?rate)?|b2b\s?rate|business\s?tax)\b/],
  b2b_tax_name: [/\b(b2b\s?tax\s?name|b2b\s?tax\s?label|b2b\s?name)\b/],
  tax_rate: [/\b(b2c\s?tax|tax\s?rate|gst\s?rate|vat\s?rate)\b/, /\btax\s?%/],
  tax_name: [/\b(b2c\s?tax\s?name|tax\s?name|tax\s?label|tax\s?title|cess)\b/],
  tax_inclusive: [/\btax\s?(type|inclusive)\b/, /^inclusive$/],
  tax_scheme_id: [/\btax\s?scheme\b/, /\bgst\s?type\b/, /\btax\s?system\b/],
  stock_quantity: [/\bopening\s?stock\b/, /\bstock\s?(qty|quantity)\b/, /^(stock|qty|quantity)$/],
  low_stock_threshold: [/\blow\s?stock\b/, /\bmin(imum)?\s?stock\b/, /\breorder\s?level\b/],
};

function classifyHeader(rawKey: string): { field: string; priority: number } | null {
  const norm = rawKey.toLowerCase().trim().replace(/\s+/g, ' ');
  // Real column headers are short labels ("Stock Quantity", "Tax Rate (%)")
  // — never full sentences. Without this guard, a descriptive paragraph
  // above a PDF table (e.g. "Contains recognized import fields (Barcode,
  // Name, Rate, Stock, etc.)...") gets mistaken for the header row, since
  // it happens to contain those exact words inside a sentence and the
  // patterns below aren't anchored to the whole string.
  const wordCount = norm.split(' ').filter(Boolean).length;
  if (norm.length > 40 || wordCount > 4) return null;
  for (const [field, patterns] of Object.entries(FIELD_PATTERNS)) {
    for (let i = 0; i < patterns.length; i++) {
      if (patterns[i].test(norm)) return { field, priority: i };
    }
  }
  return null;
}

function toTitleCase(s: string): string {
  const trimmed = s.trim();
  if (/^[A-Z0-9_-]+$/.test(trimmed)) return trimmed; // Preserve HSN, SKU, ISBN, etc.
  return trimmed.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
}

const cleanNumStr = (v: any) => String(v ?? '').replace(/,/g, '').replace(/[^0-9.-]/g, '');

function normalizeRow(raw: Record<string, any>): Omit<Product, 'id'> | null {
  const mapped: any = { barcode: '', name: '', rate: 0, unit: 'pcs' };
  const matchPriority: Record<string, number> = {};
  const extraFields: Record<string, string> = {};

  for (const [rawKey, val] of Object.entries(raw)) {
    const match = classifyHeader(rawKey);
    const hasValue = rawKey.trim() && val !== undefined && val !== '';
    if (match) {
      if (matchPriority[match.field] === undefined || match.priority < matchPriority[match.field]) {
        mapped[match.field] = val ?? '';
        matchPriority[match.field] = match.priority;
      } else if (hasValue) {
        extraFields[toTitleCase(rawKey)] = String(val).trim();
      }
    } else if (hasValue) {
      extraFields[toTitleCase(rawKey)] = String(val).trim();
    }
  }
  if (!mapped.name) return null;

  mapped.custom_fields = extraFields;
  mapped.barcode = String(mapped.barcode || '').trim();
  mapped.name = String(mapped.name).trim();
  mapped.rate = parseFloat(cleanNumStr(mapped.rate)) || 0;
  mapped.description = mapped.description ? String(mapped.description).trim() : '';
  mapped.brand = mapped.brand ? String(mapped.brand).trim() : '';
  mapped.category = mapped.category ? String(mapped.category).trim() : '';
  mapped.unit = mapped.unit ? String(mapped.unit).trim() : 'pcs';

  mapped.tax_rate = mapped.tax_rate ? parseFloat(cleanNumStr(mapped.tax_rate)) || 0 : 0;
  mapped.tax_name = mapped.tax_name ? String(mapped.tax_name).trim() : '';
  mapped.b2b_tax_rate = mapped.b2b_tax_rate !== undefined && mapped.b2b_tax_rate !== ''
    ? parseFloat(cleanNumStr(mapped.b2b_tax_rate)) || 0
    : undefined;
  mapped.b2b_tax_name = mapped.b2b_tax_name ? String(mapped.b2b_tax_name).trim() : '';

  const inclusiveRaw = String(mapped.tax_inclusive ?? '').trim().toLowerCase();
  mapped.tax_inclusive = ['yes', 'true', '1', 'inclusive', 'incl'].includes(inclusiveRaw);

  if (mapped.tax_scheme_id) {
    const raw = String(mapped.tax_scheme_id).trim().toLowerCase();
    const match = TAX_SCHEMES.find(s =>
      s.id.toLowerCase() === raw ||
      s.label.toLowerCase() === raw ||
      s.label.toLowerCase().includes(raw)
    );
    mapped.tax_scheme_id = match?.id;
  } else {
    mapped.tax_scheme_id = undefined;
  }

  mapped.stock_quantity = mapped.stock_quantity !== undefined && mapped.stock_quantity !== ''
    ? parseInt(cleanNumStr(mapped.stock_quantity)) || 0
    : 0;
  mapped.low_stock_threshold = mapped.low_stock_threshold !== undefined && mapped.low_stock_threshold !== ''
    ? parseInt(cleanNumStr(mapped.low_stock_threshold)) || 0
    : 5;

  return mapped;
}


interface Props { 
  userRole?: string;
  authUserPerms?: Record<string, boolean>;
  authUserPlan?: string;
  outletId?: number | null;
}

export default function ProductCatalogView({ userRole, authUserPerms, authUserPlan, outletId = null }: Props) {
  const perms = getPermissions(userRole, authUserPerms, authUserPlan, undefined, undefined, null);
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .excel-table-wrap::-webkit-scrollbar { width: 10px; height: 10px; display: block; }
      .excel-table-wrap::-webkit-scrollbar-track { background: #f1f5f9; }
      .excel-table-wrap::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 6px; }
      .excel-table-wrap::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
      .excel-table-wrap { scrollbar-width: thin; scrollbar-color: #cbd5e1 #f1f5f9; }
      .import-preview-scroll::-webkit-scrollbar { width: 10px; height: 10px; display: block; }
      .import-preview-scroll::-webkit-scrollbar-track { background: #f1f5f9; }
      .import-preview-scroll::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 6px; }
      .import-preview-scroll::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
      .import-preview-scroll { scrollbar-width: thin; scrollbar-color: #cbd5e1 #f1f5f9; }
      *::-webkit-scrollbar { width: 0px; height: 0px; display: none; }
      * { scrollbar-width: none; -ms-overflow-style: none; }

      .excel-header > *, .excel-row > * {
        padding: 18px 20px;
        font-size: 15px;
        border-right: 1px solid #e2e8f0;
        box-sizing: border-box;
        display: flex;
        align-items: center;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }
      .excel-header > * > *, .excel-row > * > * {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .excel-header > *:last-child, .excel-row > *:last-child { border-right: none; }
      
      /* Reset padding for the 44px Checkbox column so it never squashes into dots */
      .excel-header > *:first-child, .excel-row > *:first-child {
        padding: 0 !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
      }

      .excel-row { background: #fff; }
      .excel-row:nth-child(even) { background: #f8fafc; }
      .excel-row:hover { background: #eff6ff !important; }
      .excel-row.row-selected { background: #eff6ff !important; }
      .excel-row.row-selected > *:first-child { background: #dbeafe !important; }

      /* Inline-edit mode: inputs fill their grid cell instead of fighting
         the cell's own padding/border with their own inline ones */
      .excel-row input, .excel-row select {
        width: 100% !important;
        max-width: 100%;
        box-sizing: border-box;
      }
      .excel-row > div input {
        width: 100% !important;
      }

      /* Pin Barcode (first col) and Edit/Delete (last 2 cols) so they
         stay visible while scrolling right through custom columns.
         Header pinned cells must outrank row pinned cells (10 > 5),
         otherwise a row's sticky Barcode/Edit/Delete overlaps ON TOP
         of the sticky header during vertical scroll. */
      .excel-row > *:first-child {
        position: sticky; left: 0; z-index: 5;
        box-shadow: 2px 0 4px rgba(0,0,0,0.05);
      }
      .excel-row > *:nth-last-child(2) {
        position: sticky; right: 80px; z-index: 5;
        border-left: 1px solid #e2e8f0;
      }
      .excel-row > *:last-child {
        position: sticky; right: 0; z-index: 5;
        box-shadow: -2px 0 4px rgba(0,0,0,0.05);
      }
      .excel-header > *:first-child {
        position: sticky; left: 0; z-index: 10;
        box-shadow: 2px 0 4px rgba(0,0,0,0.05);
      }
      .excel-header > *:nth-last-child(2) {
        position: sticky; right: 80px; z-index: 10;
      }
      .excel-header > *:last-child {
        position: sticky; right: 0; z-index: 10;
        box-shadow: -2px 0 4px rgba(0,0,0,0.05);
      }
      .excel-header > *:first-child, .excel-header > *:nth-last-child(2), .excel-header > *:last-child {
        background: #f8fafc; z-index: 10;
      }
      .excel-row { background: #fff; }
      .excel-row > *:first-child, .excel-row > *:nth-last-child(2), .excel-row > *:last-child {
        background: #fff !important;
      }
      .excel-row:nth-child(even) { background: #f8fafc; }
      .excel-row:nth-child(even) > *:first-child,
      .excel-row:nth-child(even) > *:nth-last-child(2),
      .excel-row:nth-child(even) > *:last-child {
        background: #f8fafc !important;
      }
      .excel-row:hover { background: #eff6ff !important; }
      .excel-row:hover > *:first-child,
      .excel-row:hover > *:nth-last-child(2),
      .excel-row:hover > *:last-child {
        background: #eff6ff !important;
      }
    `;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);
  const [products, setProducts] = useState<Product[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(empty());
  const [editForm, setEditForm] = useState<Product | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
const [categoryCustom, setCategoryCustom] = useState(false);
const [unitCustom, setUnitCustom] = useState(false);
  // Bulk import state
  const [importStatus, setImportStatus] = useState<{ state: 'idle' | 'loading' | 'done'; inserted?: number; updated?: number; skipped?: number; message?: string }>({ state: 'idle' });
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Staged rows from an Excel/CSV/PDF file, shown for review before they're saved
  const [importPreview, setImportPreview] = useState<{
    fileName: string;
    rows: (Omit<Product, 'id'> & { custom_fields?: Record<string, string> })[];
    customCols: string[];
  } | null>(null);
  const [importSaving, setImportSaving] = useState(false);
const [customFields, setCustomFields] = useState<{label: string; value: string}[]>([]);
const [newFieldLabel, setNewFieldLabel] = useState('');
const [customFieldValues, setCustomFieldValues] = useState<Record<string, string>>({});
const [productQueue, setProductQueue] = useState<(Omit<Product, 'id'>  & { custom_fields: Record<string, string> })[]>([]);
const currSym = getCurrencySymbol(localStorage.getItem('cloudairambo_active_currency') || 'INR');

// Outlet country/region — now stored on the backend against the Outlet
// record itself (see PATCH /outlets/{id}), so it follows the outlet across
// every device/user instead of living in one browser's localStorage.
// Drives which tax scheme dropdown shows by default.
const [outletCountry, setOutletCountry] = useState<string>('');
const [outletRegion, setOutletRegion] = useState<string>('');
const [outletSettingsLoaded, setOutletSettingsLoaded] = useState(false);

// User-defined custom tax scheme for this outlet (name + optional split
// components), stored on Outlet.custom_tax_scheme. Null until the user
// creates one via the "Create custom tax" modal below.
const [customTaxScheme, setCustomTaxScheme] = useState<CustomTaxSchemeConfig | null>(null);
const [showCustomTaxModal, setShowCustomTaxModal] = useState(false);
// B2C Setup
const [customTaxMode, setCustomTaxMode] = useState<'single' | 'split'>('single');
const [customTaxModalName, setCustomTaxModalName] = useState('');
const [customTaxFlatRate, setCustomTaxFlatRate] = useState<number>(0);
const [customTaxModalComponents, setCustomTaxModalComponents] = useState<{ name: string; rate: number }[]>([{ name: '', rate: 0 }]);

// B2B Setup (Identical to B2C)
const [customB2BEnabled, setCustomB2BEnabled] = useState(false);
const [customB2BMode, setCustomB2BMode] = useState<'single' | 'split'>('single');
const [customTaxModalB2BName, setCustomTaxModalB2BName] = useState('');
const [customTaxModalB2BFlatRate, setCustomTaxModalB2BFlatRate] = useState<number>(0);
const [customTaxModalB2BComponents, setCustomTaxModalB2BComponents] = useState<{ name: string; rate: number }[]>([{ name: '', rate: 0 }]);
const [customTaxSaving, setCustomTaxSaving] = useState(false);

const openCustomTaxModal = () => {
  if (customTaxScheme) {
    // 1. Populate B2C
    setCustomTaxModalName(customTaxScheme.name);
    const comps = customTaxScheme.components || [];
    if (comps.length > 1) {
      setCustomTaxMode('split');
      setCustomTaxModalComponents(comps.map(c => ({ name: c.name, rate: c.rate })));
    } else {
      setCustomTaxMode('single');
      setCustomTaxFlatRate(comps[0]?.rate || 0);
    }

    // 2. Populate B2B
    const b2bComps = (customTaxScheme as any).b2b_components || [];
    const hasB2B = ((customTaxScheme as any).b2b_rate !== undefined && (customTaxScheme as any).b2b_rate !== null) || b2bComps.length > 0;
    setCustomB2BEnabled(hasB2B);
    setCustomTaxModalB2BName((customTaxScheme as any).b2b_name || '');
    if (b2bComps.length > 1) {
      setCustomB2BMode('split');
      setCustomTaxModalB2BComponents(b2bComps.map((c: any) => ({ name: c.name, rate: c.rate })));
    } else {
      setCustomB2BMode('single');
      setCustomTaxModalB2BFlatRate(b2bComps[0]?.rate || (customTaxScheme as any).b2b_rate || 0);
    }
  } else {
    // Default from active country scheme
    setCustomTaxModalName(defaultScheme.label.replace(/\s*\(.*?\)/, ''));
    const comps = defaultScheme.components || [];
    if (comps.length > 1) {
      setCustomTaxMode('split');
      setCustomTaxModalComponents(comps.map(c => ({ name: c.name, rate: c.rate })));
    } else {
      setCustomTaxMode('single');
      setCustomTaxFlatRate(comps[0]?.rate || defaultScheme.common_rates?.[defaultScheme.common_rates.length - 1] || 0);
    }
    setCustomB2BEnabled(false);
    setCustomB2BMode('single');
    setCustomTaxModalB2BName('');
    setCustomTaxModalB2BFlatRate(0);
    setCustomTaxModalB2BComponents([{ name: '', rate: 0 }]);
  }
  setShowCustomTaxModal(true);
};

// Shadows the imported getSchemeById for the REST of this component: every
// existing getSchemeById('custom') call anywhere below — tax display, edit
// rows, add form, breakdown popover — now transparently resolves to the
// outlet's actual named custom scheme instead of the generic placeholder,
// without having to touch each call site individually.
const [selectedSchemeId, setSelectedSchemeId] = useState<string | null>(() => {
  return localStorage.getItem(`cloudairambo_outlet_scheme_${outletId}`) || null;
});
const selectedSchemeIdRef = useRef(selectedSchemeId);
useEffect(() => { selectedSchemeIdRef.current = selectedSchemeId; }, [selectedSchemeId]);

const getSchemeById = (id: string | undefined): TaxScheme | undefined => {
  if ((id === 'custom' || id === 'outlet_custom') && customTaxScheme) {
    return { ...buildCustomScheme(customTaxScheme), id: id || 'custom' };
  }
  return getSchemeByIdBase(id);
};

// If the user explicitly picked a scheme (or picked 'outlet_custom'), use it.
// Otherwise, smoothly default to the active Country & Region tax scheme!
const countryDefaultScheme = getDefaultSchemeForCountryAndRegion(outletCountry, outletRegion);
const defaultScheme: TaxScheme = (selectedSchemeId ? getSchemeById(selectedSchemeId) : null) || countryDefaultScheme;

const availableSchemes = [
  ...(customTaxScheme ? [{ ...buildCustomScheme(customTaxScheme), id: 'outlet_custom', label: `⭐ ${customTaxScheme.name} (Custom)` }] : []),
  ...getSchemesForCountry(outletCountry).filter(s => s.id !== 'custom'),
  { id: 'custom', country: 'custom', label: 'Custom / Other', kind: 'component' as const, components: [] },
];
const prevOutletCountryRef = useRef(outletCountry);
const prevOutletRegionRef = useRef(outletRegion);
const [countryJustChanged, setCountryJustChanged] = useState(false);

// Load this outlet's saved country/region from the backend whenever the
// selected outlet changes. Guards against races: if the user switches
// outlets again before this resolves, the stale response is dropped.
useEffect(() => {
  let cancelled = false;
  setOutletSettingsLoaded(false);
  const jwt = localStorage.getItem('cloudairambo_jwt') || '';
  fetch(`${BACKEND}/outlets`, { headers: jwt ? { Authorization: `Bearer ${jwt}` } : {} })
    .then(r => r.json())
    .then(list => {
      if (cancelled || !Array.isArray(list)) return;
      const match = list.find((o: any) => o.id === outletId);
      const nextCountry = match?.country || '';
      const nextRegion = match?.region || '';
      setOutletCountry(nextCountry);
      setOutletRegion(nextRegion);
      setCustomTaxScheme(match?.custom_tax_scheme || null);

      const savedScheme = match?.tax_scheme_id
        || localStorage.getItem(`cloudairambo_outlet_scheme_${outletId}`)
        || (match?.custom_tax_scheme ? 'outlet_custom' : getDefaultSchemeForCountryAndRegion(nextCountry, nextRegion).id);
      setSelectedSchemeId(savedScheme);

      prevOutletCountryRef.current = nextCountry;
      prevOutletRegionRef.current = nextRegion;
      setOutletSettingsLoaded(true);
    })
    .catch(() => { if (!cancelled) setOutletSettingsLoaded(true); });
  return () => { cancelled = true; };
}, [outletId]);

// Silent poll — picks up country/region changes made on ANOTHER device
// (e.g. mobile app) or another browser tab, since the load above only
// runs once per outletId change and won't otherwise notice a remote edit.
const outletCountryRef = useRef(outletCountry);
const outletRegionRef = useRef(outletRegion);
useEffect(() => { outletCountryRef.current = outletCountry; }, [outletCountry]);
useEffect(() => { outletRegionRef.current = outletRegion; }, [outletRegion]);

useEffect(() => {
  const interval = setInterval(() => {
    const jwt = localStorage.getItem('cloudairambo_jwt') || '';
    fetch(`${BACKEND}/outlets`, { headers: jwt ? { Authorization: `Bearer ${jwt}` } : {} })
      .then(r => r.json())
      .then(list => {
        if (!Array.isArray(list)) return;
        const match = list.find((o: any) => o.id === outletId);
        const nextCountry = match?.country || '';
        const nextRegion = match?.region || '';
        setCustomTaxScheme(match?.custom_tax_scheme || null);
        if (match?.tax_scheme_id && match.tax_scheme_id !== selectedSchemeIdRef.current) {
          setSelectedSchemeId(match.tax_scheme_id);
        }
        if (nextCountry !== outletCountryRef.current || nextRegion !== outletRegionRef.current) {
          setOutletCountry(nextCountry);
          setOutletRegion(nextRegion);
          prevOutletCountryRef.current = nextCountry;
          prevOutletRegionRef.current = nextRegion;
        }
      })
      .catch(() => {});
  }, 5000);
  return () => clearInterval(interval);
}, [outletId]);

// Only flag "country just changed" for edits made in THIS session, after
// the initial backend load has resolved — otherwise the very first load
// (empty -> saved value) would incorrectly trigger the backfill banner.
useEffect(() => {
  if (!outletSettingsLoaded) return;
  if (prevOutletCountryRef.current !== outletCountry || prevOutletRegionRef.current !== outletRegion) {
    prevOutletCountryRef.current = outletCountry;
    prevOutletRegionRef.current = outletRegion;
    setCountryJustChanged(true);
  }
}, [outletCountry, outletRegion, outletSettingsLoaded]);

// Persists a country/region change to the outlet record itself.
const saveOutletLocation = async (country: string, region: string) => {
  if (!outletId) return;
  const jwt = localStorage.getItem('cloudairambo_jwt') || '';
  try {
    const res = await fetch(`${BACKEND}/outlets/${outletId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}) },
      body: JSON.stringify({ country, region }),
    });
    const updated = await res.json();
    // Broadcast the FULL updated outlet, not just country/region — so any
    // other open screen (Profile, IAM) can merge in whatever actually
    // changed instead of only ever hearing about country/region.
    window.dispatchEvent(new CustomEvent('outlet-location-updated', { detail: { outletId, outlet: updated, country, region } }));
  } catch (e) {
    console.warn('Failed to save outlet location:', e);
  }
};

// Persists the outlet's user-defined custom tax scheme — same PATCH
// shape/endpoint as saveOutletLocation above, just a different field.
const saveCustomTaxScheme = async (config: CustomTaxSchemeConfig) => {
  if (!outletId) return;
  const jwt = localStorage.getItem('cloudairambo_jwt') || '';
  try {
    const res = await fetch(`${BACKEND}/outlets/${outletId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}) },
      body: JSON.stringify({ custom_tax_scheme: config }),
    });
    const updated = await res.json();
    setCustomTaxScheme(config);
    window.dispatchEvent(new CustomEvent('outlet-location-updated', { detail: { outletId, outlet: updated } }));
  } catch (e) {
    console.warn('Failed to save custom tax scheme:', e);
  }
};

const handleSaveCustomTaxModal = async () => {
  if (!customTaxModalName.trim()) return;
  setCustomTaxSaving(true);
  const trimmedTaxName = customTaxModalName.trim();
  const trimmedB2BName = customTaxModalB2BName.trim();

  // Build B2C components
  let components: { name: string; rate: number }[] = [];
  if (customTaxMode === 'single') {
    components = [{ name: trimmedTaxName, rate: customTaxFlatRate || 0 }];
  } else {
    // Keep a row if it has EITHER a name OR a nonzero rate — filtering on
    // name alone silently drops a row where the user only typed a rate,
    // losing that rate entirely. Falls back to the overall B2C tax name
    // when the component's own name is left blank.
    components = customTaxModalComponents
      .filter(c => c.name.trim() || c.rate > 0)
      .map(c => ({ name: c.name.trim() || trimmedTaxName, rate: c.rate || 0 }));
  }

  // Build B2B components & total rate
  let b2bComponents: { name: string; rate: number }[] = [];
  let b2bTotalRate = 0;
  if (customB2BEnabled) {
    if (customB2BMode === 'single') {
      b2bTotalRate = customTaxModalB2BFlatRate || 0;
      b2bComponents = [{ name: trimmedB2BName || `${trimmedTaxName} B2B`, rate: b2bTotalRate }];
    } else {
      // Same fallback as the B2C split branch above — a blank component
      // name with a real rate must not be dropped.
      const b2bFallbackName = trimmedB2BName || `${trimmedTaxName} B2B`;
      b2bComponents = customTaxModalB2BComponents
        .filter(c => c.name.trim() || c.rate > 0)
        .map(c => ({ name: c.name.trim() || b2bFallbackName, rate: c.rate || 0 }));
      b2bTotalRate = b2bComponents.reduce((acc, curr) => acc + curr.rate, 0);
    }
  }

  const payload: any = {
    name: trimmedTaxName,
    components,
    ...(customB2BEnabled ? {
      // Same fallback as the component rows below — a blank B2B name
      // must not save as undefined while its components still get a name.
      b2b_name: trimmedB2BName || `${trimmedTaxName} B2B`,
      b2b_rate: b2bTotalRate,
      b2b_components: b2bComponents,
    } : {
      b2b_name: undefined,
      b2b_rate: undefined,
      b2b_components: undefined,
    }),
  };

  await saveCustomTaxScheme(payload);
  setSelectedSchemeId('outlet_custom');
  if (outletId) localStorage.setItem(`cloudairambo_outlet_scheme_${outletId}`, 'outlet_custom');
  setCustomTaxSaving(false);
  setShowCustomTaxModal(false);
};

// Picks up country/region changes saved from ANY screen (e.g. Profile's
// Country & State card) while this component is mounted, so the two
// screens never show stale data relative to each other without a refresh.
useEffect(() => {
  const handler = (e: any) => {
    if (e.detail?.outletId !== outletId) return;
    const o = e.detail.outlet;
    // Full outlet object present (from any save anywhere) — merge everything
    // this screen cares about. Falls back to the old country/region-only
    // shape for events fired before other screens are updated.
    setOutletCountry((o?.country ?? e.detail.country) || '');
    setOutletRegion((o?.region ?? e.detail.region) || '');
    if (o && 'custom_tax_scheme' in o) setCustomTaxScheme(o.custom_tax_scheme || null);
    if (e.detail?.tax_scheme_id || o?.tax_scheme_id) {
      setSelectedSchemeId(e.detail?.tax_scheme_id || o?.tax_scheme_id);
    }
  };
  window.addEventListener('outlet-location-updated', handler);
  return () => window.removeEventListener('outlet-location-updated', handler);
}, [outletId]);

const handleOutletCountryChange = (country: string) => {
  const newDefault = getDefaultSchemeForCountryAndRegion(country, '');
  setSelectedSchemeId(newDefault.id);
  if (outletId) localStorage.setItem(`cloudairambo_outlet_scheme_${outletId}`, newDefault.id);
  setOutletCountry(country);
  setOutletRegion('');
  saveOutletLocation(country, '');
};

const handleOutletRegionChange = (region: string) => {
  setOutletRegion(region);
  if (selectedSchemeId !== 'outlet_custom') {
    const regDefault = getDefaultSchemeForCountryAndRegion(outletCountry, region);
    setSelectedSchemeId(regDefault.id);
    if (outletId) localStorage.setItem(`cloudairambo_outlet_scheme_${outletId}`, regDefault.id);
  }
  saveOutletLocation(outletCountry, region);
};
const handleBulkSave = async () => {
  if (!form.name && productQueue.length === 0) { setError('Add at least one product'); return; }
  setSaving(true); setError('');
  const allProducts = [...productQueue].map(p => ({ ...p, outlet_id: outletId ?? null }));
  if (form.name) {
    allProducts.push({
      ...form,
      barcode: form.barcode || generateBarcode(),
      custom_fields: {
        ...customFieldValues,
        ...customFields.reduce((acc, cf) => { if (cf.label.trim()) acc[cf.label.trim()] = cf.value; return acc; }, {} as Record<string, string>),
      },
      outlet_id: outletId ?? null,
    });
  }
  try {
    const allBarcodes = allProducts.map(p => p.barcode);
const hasDupes = allBarcodes.length !== new Set(allBarcodes).size;
if (hasDupes) { setError('Duplicate barcodes in your list — remove them first'); setSaving(false); return; }
    const jwt = localStorage.getItem('cloudairambo_jwt') || '';
    const res = await fetch(`${BACKEND}/products/bulk`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
      },
      body: JSON.stringify(allProducts),
    });
    if (!res.ok) { const e = await res.json(); setError(e.detail || 'Failed to save'); }
    else {
      const result = await res.json();
      postLog('PRODUCT_BULK_SAVED', `${result.inserted} product(s) saved | ${result.skipped} skipped`);
      setImportStatus({ state: 'done', inserted: result.inserted, skipped: result.skipped });
      setForm(empty()); setShowAdd(false); setProductQueue([]);
      setCustomFields([]); setNewFieldLabel(''); setCustomFieldValues({}); setCategoryCustom(false); setUnitCustom(false);
      await load();
    }
  } catch { setError('Network error'); }
  setSaving(false);
};
  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const jwt = localStorage.getItem('cloudairambo_jwt') || '';
      const qs = outletId ? `?outlet_id=${outletId}` : '';
      const res = await fetch(`${BACKEND}/products/${qs}`, {
        headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
      });
      const data = await res.json();
      setProducts(prev => (silent && JSON.stringify(prev) === JSON.stringify(data)) ? prev : data);
    } catch { if (!silent) setError('Failed to load products'); }
    if (!silent) setLoading(false);
  };

  useEffect(() => { load(); }, [outletId]);

  // Silent background refresh — picks up products added/edited from the
  // mobile app or another tab without the user having to reload the page.
  // Skips while the add form or an inline row edit is open so it doesn't
  // clobber unsaved input.
  const showAddRef = useRef(showAdd);
  const editingIdRef = useRef(editingId);
  useEffect(() => { showAddRef.current = showAdd; }, [showAdd]);
  useEffect(() => { editingIdRef.current = editingId; }, [editingId]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (showAddRef.current || editingIdRef.current !== null) return;
      load(true);
    }, 5000);
    return () => clearInterval(interval);
  }, [outletId]);

  const handleAdd = async () => {
    if (!form.name) { setError('Product name is required'); return; }
    const finalForm = {
  ...form,
  barcode: form.barcode || generateBarcode(),
  custom_fields: {
    ...customFieldValues,
    ...customFields.reduce((acc, cf) => {
      if (cf.label.trim()) acc[cf.label.trim()] = cf.value;
      return acc;
    }, {} as Record<string, string>),
  },
  outlet_id: outletId ?? null,
};
    setSaving(true); setError('');
    try {
      const jwt = localStorage.getItem('cloudairambo_jwt') || '';
      const res = await fetch(`${BACKEND}/products/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
        },
        body: JSON.stringify(finalForm),
      });
      if (!res.ok) {
        const e = await res.json();
        setError(e.detail || 'Failed to save');
      } else {
        postLog('PRODUCT_ADDED', `Product added: ${finalForm.name} | ${finalForm.barcode}${finalForm.brand ? ` | ${finalForm.brand}` : ''}${finalForm.category ? ` | ${finalForm.category}` : ''} | ${currSym}${finalForm.rate}`);
        setForm(empty());
        setShowAdd(false);
        await load();
      }
    } catch { setError('Network error'); }
    setSaving(false);
  };

 const handleUpdate = async (id: number) => {
    if (!editForm) return;
    setSaving(true);
    try {
      const jwt = localStorage.getItem('cloudairambo_jwt') || '';
      const res = await fetch(`${BACKEND}/products/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
        },
        body: JSON.stringify(editForm),
      });
      if (!res.ok) {
        const e = await res.json();
        alert(e.detail || 'Update failed');
        setSaving(false);
        return;
      }
      postLog('PRODUCT_UPDATED', `Product updated: ${editForm.name} | ${editForm.barcode} | ${currSym}${editForm.rate}`);
      setEditingId(null);
      await load();
    } catch { alert('Network error'); }
    setSaving(false);
  };

const handleDelete = async (id: number) => {
  if (!confirm('Delete this product?')) return;
  const jwt = localStorage.getItem('cloudairambo_jwt') || '';
  const product = products.find(p => p.id === id);
  const res = await fetch(`${BACKEND}/products/${id}`, {
    method: 'DELETE',
    headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
  });
  if (!res.ok) {
    const e = await res.json();
    alert(e.detail || 'Delete failed');
    return;
  }
  postLog('PRODUCT_DELETED', `Product deleted: ${product?.name || `#${id}`} | ${product?.barcode || ''}`);
  await load();
};

const toggleSelect = (id: number) => {
  setSelectedIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
};

const toggleSelectAll = () => {
  setSelectedIds(prev => prev.size === filtered.length ? new Set() : new Set(filtered.map(p => p.id)));
};

const handleBulkDelete = async () => {
  if (selectedIds.size === 0) return;
  if (!confirm(`Delete ${selectedIds.size} selected product(s)? This cannot be undone.`)) return;
  const jwt = localStorage.getItem('cloudairambo_jwt') || '';
  await Promise.all(Array.from(selectedIds).map(id =>
    fetch(`${BACKEND}/products/${id}`, {
      method: 'DELETE',
      headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
    })
  ));
  postLog('PRODUCT_BULK_DELETED', `${selectedIds.size} product(s) deleted`);
  setSelectedIds(new Set());
  await load();
};

  // Parse Excel/CSV/PDF and stage rows in a preview — nothing is saved yet
  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setImportStatus({ state: 'loading' });
    setImportPreview(null);

    try {
   const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });
      const parsed = rows.map(normalizeRow).filter(Boolean) as (Omit<Product, 'id'> & { custom_fields?: Record<string, string> })[];
      if (parsed.length === 0) {
        setImportStatus({ state: 'done', message: 'No valid rows found. Make sure columns include "Barcode" and "Name".' });
        return;
      }
      const customCols = Array.from(new Set(parsed.flatMap(p => Object.keys(p.custom_fields || {}))));

      setImportStatus({ state: 'idle' });
      setImportPreview({ fileName: file.name, rows: parsed, customCols });
    } catch (err: any) {
      console.error('Import error:', err); // full details stay in browser console for debugging
      setImportStatus({
        state: 'done',
        message: "We couldn't read that file. Please check it's a valid Excel or CSV file and try again.",
      });
    }
  };

  // User reviewed the preview and confirmed — now actually save to the backend.
  // If any barcodes in the file already exist in the catalog, ask whether to
  // update those existing products with the new file's data or just skip them
  // (leaving the existing product untouched) and only add the new ones.
  const handleConfirmImport = async () => {
    if (!importPreview) return;
    setImportSaving(true);
    try {
      const existingBarcodes = new Set(products.map(p => p.barcode));
      const dupeCount = importPreview.rows.filter(r => r.barcode && existingBarcodes.has(r.barcode)).length;
      let upsert = false;
      if (dupeCount > 0) {
        upsert = confirm(
          `${dupeCount} product${dupeCount !== 1 ? 's' : ''} in this file already exist in your catalog (matching barcode).\n\n` +
          `Click OK to UPDATE those existing products with the new file's data.\n` +
          `Click Cancel to KEEP the existing products unchanged and only add the new ones.`
        );
      }

      const toSave = importPreview.rows.map(p => ({
        ...p,
        barcode: p.barcode || generateBarcode(),
        tax_scheme_id: p.tax_scheme_id || defaultScheme.id,
        outlet_id: outletId ?? null,
      }));
      const jwt = localStorage.getItem('cloudairambo_jwt') || '';
      const res = await fetch(`${BACKEND}/products/bulk?upsert=${upsert}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
        },
        body: JSON.stringify(toSave),
      });
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      const result = await res.json();
      postLog('PRODUCT_IMPORTED', `Import (${importPreview.fileName}): ${result.inserted} added, ${result.updated || 0} updated, ${result.skipped} skipped`);
      setImportStatus({ state: 'done', inserted: result.inserted, updated: result.updated, skipped: result.skipped });
      setImportPreview(null);
      await load();
    } catch (err: any) {
      console.error('Import save error:', err);
      setImportStatus({ state: 'done', message: "Couldn't save the imported products. Please try again." });
    }
    setImportSaving(false);
  };

  const handleCancelImport = () => setImportPreview(null);

  // Remove a single staged row from the preview before saving
  const handleRemovePreviewRow = (idx: number) => {
    setImportPreview(prev => prev ? { ...prev, rows: prev.rows.filter((_, i) => i !== idx) } : prev);
  };

  // Export current catalog to Excel
  const handleExport = () => {
    if (products.length === 0) { alert('No products to export.'); return; }
    const rows = products.map(p => ({
      Barcode: p.barcode,
      Name: p.name,
      Brand: p.brand || '',
      Category: p.category || '',
      Rate: p.rate,
      'Tax Rate (%)': p.tax_rate || 0,
      'Tax Name': p.tax_name || '',
      'B2B Tax Rate (%)': p.b2b_tax_rate ?? '',
      'B2B Tax Name': p.b2b_tax_name || '',
      'Tax Type': p.tax_inclusive ? 'Inclusive' : 'Exclusive',
      'Tax Scheme': getSchemeById(p.tax_scheme_id)?.label || '',
      Unit: p.unit || '',
      Stock: p.stock_quantity ?? 0,
      'Low Stock Alert': p.low_stock_threshold ?? 5,
      Description: p.description || '',
      ...Object.fromEntries(Object.entries(p.custom_fields || {})),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const fixedCols = [
      { wch: 18 }, // Barcode
      { wch: 30 }, // Name
      { wch: 18 }, // Brand
      { wch: 16 }, // Category
      { wch: 10 }, // Rate
      { wch: 12 }, // Tax Rate (%)
      { wch: 14 }, // Tax Name (B2C)
      { wch: 15 }, // B2B Tax Rate (%)
      { wch: 15 }, // B2B Tax Name
      { wch: 12 }, // Tax Type
      { wch: 24 }, // Tax Scheme
      { wch: 8 },  // Unit
      { wch: 10 }, // Stock
      { wch: 14 }, // Low Stock Alert
      { wch: 35 }, // Description
    ];
    const customColCount = Array.from(new Set(products.flatMap(p => Object.keys(p.custom_fields || {})))).length;
    ws['!cols'] = [...fixedCols, ...Array(customColCount).fill({ wch: 16 })];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Products');
    XLSX.writeFile(wb, `ProductCatalog_${Date.now().toString().slice(-6)}.xlsx`);
  };

  // Download blank template
  // Called by QuickScannerModal for every scan (camera, phone, manual, OCR).
  // Existing barcode → bump stock instead of duplicating. New barcode → create.
  const handleScanAddItem = async (description: string, qty: number, rate: number, barcode?: string) => {
    const code = barcode || generateBarcode();
    const jwt = localStorage.getItem('cloudairambo_jwt') || '';
    const existing = products.find(p => p.barcode === code);

    if (existing) {
      try {
        await fetch(`${BACKEND}/products/${existing.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}) },
          body: JSON.stringify({ stock_quantity: (existing.stock_quantity ?? 0) + (qty || 1) }),
        });
        postLog('PRODUCT_UPDATED', `Stock +${qty || 1} via scan: ${existing.name} | ${code}`);
        await load(true);
      } catch { /* network error — user can rescan */ }
      return;
    }

    try {
      const res = await fetch(`${BACKEND}/products/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}) },
        body: JSON.stringify({
          barcode: code,
          name: description,
          rate: rate || 0,
          unit: 'pcs',
          stock_quantity: qty || 1,
          low_stock_threshold: 5,
          outlet_id: outletId ?? null,
        }),
      });
      if (res.ok) {
        postLog('PRODUCT_ADDED', `Product added via scan: ${description} | ${code}${rate ? ` | ${currSym}${rate}` : ''}`);
        await load(true);
      }
    } catch { /* network error — user can rescan */ }
  };

  const handleDownloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      ['Barcode', 'Name', 'Brand', 'Category', 'Rate', 'Tax Rate (%)', 'Tax Name', 'B2B Tax Rate (%)', 'B2B Tax Name', 'Tax Type', 'Tax Scheme', 'Unit', 'Stock', 'Low Stock Alert', 'Description'],
      ['8901234567890', 'Example Product', 'BrandName', 'Electronics', 299.99, 18, 'Retail GST', 18, 'Corporate GST', 'Exclusive', 'India GST', 'pcs', 50, 5, 'Optional description'],
      ['', 'Another Product', '', 'Grocery & Retail', 99, 5, 'VAT', '', '', 'Inclusive', 'UK VAT', 'kg', 100, 10, ''],
    ]);
    ws['!cols'] = [
      { wch: 18 }, { wch: 30 }, { wch: 18 }, { wch: 16 }, { wch: 10 },
      { wch: 12 }, { wch: 14 }, { wch: 15 }, { wch: 15 }, { wch: 12 }, { wch: 24 },
      { wch: 8 },  { wch: 10 }, { wch: 14 }, { wch: 35 }
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Products');
    XLSX.writeFile(wb, 'ProductCatalog_Template.xlsx');
  };
const [hiddenKeys, setHiddenKeys] = useState<string[]>([]);
const [taxPopover, setTaxPopover] = useState<{ productId: number; x: number; y: number } | null>(null);
const [showTaxBackfill, setShowTaxBackfill] = useState(false);
const [backfillScheme, setBackfillScheme] = useState<string>('');
const [backfillBusy, setBackfillBusy] = useState(false);
const [backfillTarget, setBackfillTarget] = useState<'missing' | 'all'>('all');
const [backfillScope, setBackfillScope] = useState<'both' | 'b2c' | 'b2b'>('both');

const legacyTaxCount = products.filter(p => !p.tax_scheme_id || p.tax_scheme_id === 'custom').length;

const handleBackfillTaxScheme = async () => {
  if (!backfillScheme) return;
  setBackfillBusy(true);
  const jwt = localStorage.getItem('cloudairambo_jwt') || '';
  const targets = backfillTarget === 'all'
    ? products
    : products.filter(p => !p.tax_scheme_id || p.tax_scheme_id === 'custom');

  // Resolve target scheme details
  const targetScheme = getSchemeById(backfillScheme) || defaultScheme;
  const isCustom = backfillScheme === 'outlet_custom' || (customTaxScheme && backfillScheme === 'custom');

  const defB2CName = isCustom && customTaxScheme
    ? customTaxScheme.name
    : (targetScheme.components?.[0]?.name || targetScheme.label.replace(/\s*\(.*?\)/, ''));

  const defB2CRate = isCustom && customTaxScheme
    ? (customTaxScheme.components?.[0]?.rate ?? 0)
    : (targetScheme.components?.[0]?.rate ?? targetScheme.common_rates?.[targetScheme.common_rates.length - 1] ?? 0);

  // If custom tax has a B2B name, use it; otherwise B2B name matches B2C (e.g. PPN or VAT)
  const defB2BName = isCustom
    ? ((customTaxScheme as any)?.b2b_name || `${defB2CName} B2B`)
    : defB2CName;

  // Use null instead of undefined so JSON.stringify doesn't drop the key!
  const defB2BRate = isCustom && (customTaxScheme as any)?.b2b_rate !== undefined
    ? Number((customTaxScheme as any).b2b_rate)
    : null;

  const results = await Promise.all(targets.map(async p => {
    try {
      const updateBody: any = { tax_scheme_id: backfillScheme };

      // Apply B2C if chosen
      if (backfillScope === 'b2c' || backfillScope === 'both') {
        updateBody.tax_name = defB2CName;
        updateBody.tax_rate = defB2CRate;
      }

      // Apply B2B if chosen (null clears custom rate and returns it to standard)
      if (backfillScope === 'b2b' || backfillScope === 'both') {
        updateBody.b2b_tax_name = defB2BName;
        updateBody.b2b_tax_rate = defB2BRate;
      }

      const res = await fetch(`${BACKEND}/products/${p.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}) },
        body: JSON.stringify(updateBody),
      });
      return res.ok;
    } catch {
      return false;
    }
  }));

  const succeeded = results.filter(Boolean).length;
  const failed = results.length - succeeded;

  postLog('PRODUCT_TAX_SCHEME_BACKFILLED', `${succeeded} product(s) updated to ${targetScheme.label} (${backfillScope})`);
  setShowTaxBackfill(false);
  setBackfillBusy(false);
  await load();

  if (failed > 0) {
    alert(`${succeeded} product(s) updated, but ${failed} failed to update. Check your connection and try again.`);
  }
};
const [showScanner, setShowScanner] = useState(false);
const [showBarcodePicker, setShowBarcodePicker] = useState(false);
const [pickerSelected, setPickerSelected] = useState<Set<number>>(new Set());
const [pickerQty, setPickerQty] = useState<Record<number, number>>({});
const [pickerPreset, setPickerPreset] = useState<keyof typeof LABEL_PRESETS>('avery_2x4');
const [pdfBusy, setPdfBusy] = useState(false);
// Which fields to include on each printed label — same shape as the app.
const [labelFields, setLabelFields] = useState({
  name: true,
  brand: true,
  price: true,
  unit: true,
  barcodeNumber: true,
});
const [usbConnecting, setUsbConnecting] = useState(false);
const [btConnecting, setBtConnecting] = useState(false);
const [addingColumn, setAddingColumn] = useState(false);
const [newColumnName, setNewColumnName] = useState('');
const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
const [showPrintSettings, setShowPrintSettings] = useState(false);
const [useUSBPrinter, setUseUSBPrinter] = useState(() =>
  localStorage.getItem('cloudairambo_catalog_usb_print') === 'true'
);
// Physical roll width of the connected USB label printer, in mm — completely
// separate from pickerPreset (which only controls PDF sheet layout). We
// can't query this from the device over WebUSB, so it's a one-time user
// setting that persists, defaulting to the most common small label roll.
const [usbRollWidthMm, setUsbRollWidthMm] = useState<number>(() =>
  parseFloat(localStorage.getItem('cloudairambo_usb_roll_width_mm') || '50')
);
const [usbConnected, setUsbConnected] = useState(false);
const [useBTPrinter, setUseBTPrinter] = useState(() =>
  localStorage.getItem('cloudairambo_catalog_bt_print') === 'true'
);
const [btConnected, setBtConnected] = useState(() => !!getBTPrinterName());
// Which command language the connected BT printer speaks. Most dedicated
// barcode/label printers (Zebra, TSC, GoDEX, generic "label printer" BT
// devices) speak ZPL; receipt/thermal roll printers speak ESC/POS.
const [btPrinterLang, setBtPrinterLang] = useState<'escpos' | 'zpl'>(() =>
  (localStorage.getItem('cloudairambo_bt_printer_lang') as 'escpos' | 'zpl') || 'zpl'
);

useEffect(() => {
  tryAutoReconnectUSB().then(ok => setUsbConnected(ok || isUSBPrinterConnected()));
}, []);


  const filtered = products.filter(p => {
    if (!search) return true;
    const t = search.toLowerCase();
    return p.name.toLowerCase().includes(t) || p.barcode.toLowerCase().includes(t) || (p.brand || '').toLowerCase().includes(t) || (p.category || '').toLowerCase().includes(t);
  });

  const lowStockCount = products.filter(p => (p.stock_quantity ?? 0) > 0 && (p.stock_quantity ?? 0) <= (p.low_stock_threshold ?? 5)).length;
  const outOfStockCount = products.filter(p => (p.stock_quantity ?? 0) === 0).length;
  const healthyCount = products.filter(p => (p.stock_quantity ?? 0) > (p.low_stock_threshold ?? 5)).length;

const handleAddColumn = async () => {
  if (!newColumnName.trim()) return;
  const jwt = localStorage.getItem('cloudairambo_jwt') || '';
  await Promise.all(products.map(p =>
    fetch(`${BACKEND}/products/${p.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
      },
      body: JSON.stringify({
        custom_fields: { ...(p.custom_fields || {}), [newColumnName.trim()]: '' }
      }),
    })
  ));
  postLog('PRODUCT_COLUMN_ADDED', `Custom column added: ${newColumnName.trim()}`);
  setNewColumnName('');
  setAddingColumn(false);
  await load();
};


// Standard label sizes, in mm
const LABEL_PRESETS = {
  avery_2x4: { name: 'Avery 5163 (2" × 4")', w: 101.6, h: 50.8, cols: 2, rows: 5, pageW: 215.9, pageH: 279.4 },
  thermal_50x25: { name: 'Thermal Roll (50 × 25mm)', w: 50, h: 25, cols: 1, rows: 1, pageW: 50, pageH: 25 },
  thermal_40x30: { name: 'Thermal Roll (40 × 30mm)', w: 40, h: 30, cols: 1, rows: 1, pageW: 40, pageH: 30 },
} as const;

// EAN-13 needs exactly 13 digits (12 data + 1 check digit).
// bwip-js validates/generates the check digit itself.
const isEAN13 = (code: string) => /^\d{13}$/.test(code);

// Generates one barcode <img> as a data URL for embedding in the label sheet.
// Uses EAN-13 for real 13-digit retail codes, falls back to Code128 for
// custom/text codes like the client-generated "PRD-xxxxxxx" fallback.
const barcodeDataUrl = (barcode: string): string | null => {
  const canvas = document.createElement('canvas');
  try {
   bwipjs.toCanvas(canvas, {
  bcid: isEAN13(barcode) ? 'ean13' : 'code128',
  text: barcode,
  scale: 2,
  height: 7,
  includetext: false,   // no baked-in text — we render it separately as HTML
});
    return canvas.toDataURL();
  } catch (e) {
    console.warn('Barcode render failed for', barcode, e);
    return null;
  }
};

// Opens the inline picker modal — no popup window, no window.write.
// Selection defaults to everything currently filtered, qty 1 each.
const openBarcodePicker = () => {
  if (products.length === 0) { alert('No products to print.'); return; }
  const items = filtered.length > 0 ? filtered : products;
  setPickerSelected(new Set(items.map(p => p.id)));
  setPickerQty(Object.fromEntries(items.map(p => [p.id, 1])));
  setShowBarcodePicker(true);
};

const togglePickerSelect = (id: number) => {
  setPickerSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
};

const setPickerQtyFor = (id: number, val: number) => {
  const clamped = Math.max(1, Math.min(999, val || 1));
  setPickerQty(prev => ({ ...prev, [id]: clamped }));
};

const getPickerItems = () => {
  const items = filtered.length > 0 ? filtered : products;
  return items.filter(p => pickerSelected.has(p.id));
};

const pickerTotalLabels = () =>
  getPickerItems().reduce((sum, p) => sum + (pickerQty[p.id] ?? 1), 0);

// Builds the PDF from current picker selection/quantities
const buildBarcodePdf = (): jsPDF => {
  const preset = LABEL_PRESETS[pickerPreset];
  const isSheet = preset.cols > 1 || preset.rows > 1;
  const docW = isSheet ? preset.pageW : preset.w;
  const docH = isSheet ? preset.pageH : preset.h;
  const orientation: 'portrait' | 'landscape' = docW > docH ? 'landscape' : 'portrait';
  const doc = new jsPDF({
    unit: 'mm',
    format: [docW, docH],
    orientation,
  });
  const marginX = isSheet ? (preset.pageW - preset.cols * preset.w) / (preset.cols + 1) : 0;
  const marginY = isSheet ? (preset.pageH - preset.rows * preset.h) / (preset.rows + 1) : 0;
  const perPage = preset.cols * preset.rows;

  const expanded = getPickerItems().flatMap(p => Array(pickerQty[p.id] ?? 1).fill(p));

  expanded.forEach((p, i) => {
    const posInPage = i % perPage;
    const row = Math.floor(posInPage / preset.cols);
    const col = posInPage % preset.cols;

    if (i > 0 && posInPage === 0) {
      doc.addPage([docW, docH], orientation);
    }

    const x = marginX + col * (preset.w + marginX);
    const y = marginY + row * (preset.h + marginY);

    doc.setDrawColor(200, 200, 200);
    doc.setLineDashPattern([1, 1], 0);
    doc.rect(x, y, preset.w, preset.h);
    doc.setLineDashPattern([], 0);

    let cursorY = y + preset.h * 0.14;

    if (labelFields.name && p.name) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(preset.h > 30 ? 9 : 6.5);
      doc.setTextColor(0, 0, 0);
      doc.text(p.name, x + preset.w / 2, cursorY, { align: 'center', maxWidth: preset.w - 4 });
      cursorY += preset.h * 0.09;
    }
    if (labelFields.brand && p.brand) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(preset.h > 30 ? 7 : 5.5);
      doc.setTextColor(100, 116, 139);
      doc.text(p.brand, x + preset.w / 2, cursorY, { align: 'center', maxWidth: preset.w - 4 });
      doc.setTextColor(0, 0, 0);
      cursorY += preset.h * 0.07;
    }

    const dataUrl = barcodeDataUrl(p.barcode);
    if (dataUrl) {
      const imgW = preset.w * 0.85;
      const imgH = preset.h * 0.32;
      const imgY = cursorY;
      doc.addImage(dataUrl, 'PNG', x + (preset.w - imgW) / 2, imgY, imgW, imgH);
      cursorY = imgY + imgH + 4;

      if (labelFields.barcodeNumber) {
        doc.setFont('courier', 'bold');
        doc.setFontSize(preset.h > 30 ? 12 : 9);
        doc.setTextColor(0, 0, 0);
        doc.text(p.barcode, x + preset.w / 2, cursorY, { align: 'center' });
        cursorY += preset.h * 0.08;
      }
    } else {
      doc.setFontSize(6);
      doc.setTextColor(220, 38, 38);
      doc.text(`Invalid: ${p.barcode}`, x + preset.w / 2, y + preset.h * 0.5, { align: 'center', maxWidth: preset.w - 4 });
      doc.setTextColor(0, 0, 0);
    }

    if (labelFields.price && p.rate > 0) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(preset.h > 30 ? 10 : 7.5);
      doc.text(
        `MRP ${currSym}${p.rate.toFixed(2)}${labelFields.unit && p.unit ? ' / ' + p.unit : ''}`,
        x + preset.w / 2, y + preset.h * 0.92, { align: 'center' }
      );
    } else if (labelFields.unit && p.unit) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(preset.h > 30 ? 8 : 6);
      doc.setTextColor(100, 116, 139);
      doc.text(`per ${p.unit}`, x + preset.w / 2, y + preset.h * 0.92, { align: 'center' });
      doc.setTextColor(0, 0, 0);
    }
  });

  return doc;
};

const handlePickerPrintPDF = () => {
  if (pickerSelected.size === 0) { alert('Select at least one product.'); return; }

  // Open the tab FIRST, synchronously, directly inside the click handler —
  // before any PDF building runs. This is what actually ties the new tab
  // to the user gesture. If we build the PDF first and THEN open/navigate
  // a tab afterward, some browsers no longer trust that as gesture-linked
  // once any real work (canvas rasterization here) has happened in between,
  // and silently leave the tab on about:blank instead of erroring.
  const newTab = window.open('', '_blank');

  setPdfBusy(true);
  try {
    const doc = buildBarcodePdf();
    const blobUrl = doc.output('bloburl') as string;

    if (newTab) {
      newTab.location.href = blobUrl;
    } else {
      // Popup was actually blocked — don't leave a dead tab, just download.
      alert('Your browser blocked the print tab — downloading the PDF instead. Open it from your downloads to print.');
      doc.save(`Barcodes_${Date.now().toString().slice(-6)}.pdf`);
    }
  } catch (e) {
    console.error('PDF build failed:', e);
    if (newTab) newTab.close();
    alert("Couldn't generate the PDF. Please try again.");
  } finally {
    setPdfBusy(false);
  }
};

const handlePickerDownloadPDF = () => {
  if (pickerSelected.size === 0) { alert('Select at least one product.'); return; }
  setPdfBusy(true);
  try {
    const doc = buildBarcodePdf();
    doc.save(`Barcodes_${Date.now().toString().slice(-6)}.pdf`);
  } finally { setPdfBusy(false); }
};

// Builds a full standalone HTML file — barcode image + name + MRP per label —
// that anyone can open in a browser and print, no app needed.
const buildBarcodeLabelsFullHtml = (): string => {
  const cards = getPickerItems().flatMap(p => {
    const img = barcodeDataUrl(p.barcode);
    const qty = pickerQty[p.id] ?? 1;
    const card = `
      <div class="label">
        <div class="name">${(p.name || '').replace(/</g, '&lt;')}</div>
        ${img
          ? `<img src="${img}" class="barcode-img" />`
          : `<div class="invalid">Invalid barcode</div>`}
        <div class="code">${p.barcode}</div>
        ${p.rate > 0 ? `<div class="mrp">MRP ${currSym}${p.rate.toFixed(2)}</div>` : ''}
      </div>`;
    return Array(qty).fill(card);
  });

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<title>Barcode Labels</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: Arial, sans-serif; margin: 16px; background: #f8fafc; }
  .grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 10px;
  }
  .label {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 10px;
    text-align: center;
    page-break-inside: avoid;
  }
  .name { font-size: 12px; font-weight: 700; color: #0f172a; margin-bottom: 4px;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .barcode-img { width: 100%; max-width: 160px; height: auto; margin: 4px 0; }
  .brand { font-size: 10px; color: #64748b; margin-bottom: 2px; }
  .code { font-size: 10px; font-family: monospace; letter-spacing: 1px; color: #475569; }
  .mrp { font-size: 12px; font-weight: 700; color: #16a34a; margin-top: 4px; }
  .unit { font-size: 10px; color: #94a3b8; }
  .invalid { font-size: 11px; color: #ef4444; padding: 20px 0; }
  @media print {
    body { background: #fff; margin: 0; }
    .label { border: 1px dashed #cbd5e1; }
  }
</style>
</head>
<body>
  <div class="grid">
    ${cards.join('\n')}
  </div>
</body>
</html>`;
};

const handlePickerDownloadHTML = () => {
  if (pickerSelected.size === 0) { alert('Select at least one product.'); return; }
  const html = buildBarcodeLabelsFullHtml();
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `Barcodes_${Date.now().toString().slice(-6)}.html`;
  a.click();
  URL.revokeObjectURL(url);
};

// Builds the same label-sheet HTML your USB thermal path expects,
// straight from the picker's current selection/quantities.
const buildBarcodeLabelsHtml = (): string => {
  let html = '';
  getPickerItems().forEach(p => {
    const qty = pickerQty[p.id] ?? 1;
    const img = barcodeDataUrl(p.barcode);
    const single = `<div style="display:block;box-sizing:border-box;background:#ffffff;border-radius:6px;padding:6px 0 6px 8px;margin:0 0 6px 0;width:190px;">
      ${labelFields.name && p.name ? `<div style="font-size:11px;font-weight:700;color:#0f172a;width:90px;">${p.name}</div>` : ''}
      ${labelFields.brand && p.brand ? `<div style="font-size:9px;font-weight:700;color:#000000;width:90px;">${p.brand}</div>` : ''}
      ${img ? `
        <img src="${img}" style="display:block;width:90px;height:auto;image-rendering:pixelated;" />
        ${labelFields.barcodeNumber ? `<div style="font-size:9px;font-family:monospace;letter-spacing:1px;text-align:center;width:90px;margin-top:2px;">${p.barcode}</div>` : ''}
      ` : `<div style="font-size:11px;color:#ef4444;">Invalid barcode: ${p.barcode}</div>`}
      ${labelFields.price && p.rate > 0 ? `<div style="font-size:11px;font-weight:700;color:#000000;width:90px;">MRP ${currSym}${p.rate.toFixed(2)}</div>` : ''}
      ${labelFields.unit && p.unit ? `<div style="font-size:9px;font-weight:700;color:#000000;width:90px;">per ${p.unit}</div>` : ''}
    </div>`;
    for (let i = 0; i < qty; i++) html += single;
  });
  return html;
};

// Connects a USB printer (if not already) then prints immediately —
// so the user doesn't have to leave the picker to set up the printer first.
const handlePickerConnectAndPrintUSB = async () => {
  if (pickerSelected.size === 0) { alert('Select at least one product.'); return; }
  setUsbConnecting(true);
  try {
    const ok = await connectUSBPrinter();
    setUsbConnected(ok);
    if (!ok) { alert('Could not connect to a USB printer.'); return; }
    localStorage.setItem('cloudairambo_catalog_usb_print', 'true');
    await handlePickerPrintUSB();
  } finally {
    setUsbConnecting(false);
  }
};

// Prints via the already-connected USB thermal printer, directly in this
// window — no popup, no window.opener bridging required anymore.
const handlePickerPrintUSB = async () => {
  if (pickerSelected.size === 0) { alert('Select at least one product.'); return; }
  const html = buildBarcodeLabelsHtml();
  const container = document.createElement('div');
  container.id = 'catalog-print-labels-usb-inline';
  container.style.cssText = 'position:fixed;left:0;top:0;z-index:-1;background:#fff;';
  container.innerHTML = html;
  document.body.appendChild(container);
  try {
    // Convert the printer's actual roll width (mm) to dots at 203dpi — deliberately
  // NOT derived from pickerPreset, since that's the PDF sheet label size (e.g. a
  // 4" Avery label) and has nothing to do with the physical USB printer's head width.
    const printerWidthDots = Math.round(usbRollWidthMm * (203 / 25.4));
    await printElementUSB(container.id, printerWidthDots);
  } catch (e) {
    alert('USB print failed. Please try Print via Browser instead.');
    console.warn('USB label print failed:', e);
  } finally {
    document.body.removeChild(container);
  }
};

// Prints label images (name + barcode raster + price) over an already
// paired Bluetooth thermal printer.
const handlePickerPrintBT = async () => {
  if (pickerSelected.size === 0) { alert('Select at least one product.'); return; }
  const pickerItems = getPickerItems();

  let ok = false;
  if (btPrinterLang === 'zpl') {
    // ZPL path — printer draws the barcode itself from raw text, no image needed.
    const items = pickerItems.map(p => ({
      name: p.name,
      brand: p.brand,
      rate: p.rate,
      unit: p.unit,
      barcode: p.barcode,
      qty: pickerQty[p.id] ?? 1,
    }));
    ok = await printBarcodeLabelsZPL(items, { currSym: currSym, fields: labelFields });
  } else {
    // ESC/POS path — barcode sent as a rasterized image.
    const items = pickerItems
      .map(p => ({
        name: p.name,
        brand: p.brand,
        rate: p.rate,
        unit: p.unit,
        barcodeImgDataUrl: barcodeDataUrl(p.barcode) || '',
        barcode: p.barcode,
        qty: pickerQty[p.id] ?? 1,
      }))
      .filter(i => i.barcodeImgDataUrl);
    if (items.length === 0) { alert('No valid barcodes to print.'); return; }
    ok = await printBarcodeLabelsBT(items, { currSym: currSym, fields: labelFields });
  }

  if (!ok) alert('Bluetooth print failed. Make sure the printer is connected and try again.');
};

// Opens the Bluetooth device picker (if not already connected) then prints.
const handlePickerConnectAndPrintBT = async () => {
  if (pickerSelected.size === 0) { alert('Select at least one product.'); return; }
  setBtConnecting(true);
  try {
    await handlePickerPrintBT();
    localStorage.setItem('cloudairambo_catalog_bt_print', 'true');
    setBtConnected(!!getBTPrinterName());
  } finally {
    setBtConnecting(false);
  }
};
const allCustomKeys = Array.from(new Set(products.flatMap(p => Object.keys(p.custom_fields || {}))));
const customKeys = allCustomKeys.filter(k => !hiddenKeys.includes(k));
// Fixed columns: Checkbox(44) + Barcode(300) + Brand(160) + Category(140) +
// Rate(120) + B2C Tax(150) + B2B Tax(140) + Unit(100) + Stock(90) + Min(80) + Edit(90) +
// Delete(90) = 1504, plus 300 minimum for the Name column (1fr) and 150 per custom column.
const tableMinWidth = 1504 + 300 + customKeys.length * 150;
  return (
    <div style={{ 
      maxWidth: '100%', 
      width: '100%', 
      margin: '0 auto', 
      padding: '24px 20px', 
      paddingBottom: 80, 
      boxSizing: 'border-box' as const,
      fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#0f172a' }}>Product Catalog</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>
            {products.length} products · Scanned products auto-save here
          </p>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>

          {/* Download Template */}
          <button onClick={handleDownloadTemplate}
            title="Download blank Excel template with correct column headers"
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 14px', background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 10, fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
            <FileSpreadsheet size={14} style={{ color: '#10b981' }} /> Template
          </button>

          {/* Import Excel/CSV */}
          {perms.canImportProducts && <>
            <button onClick={() => fileInputRef.current?.click()}
              disabled={importStatus.state === 'loading'}
             title="Import products from Excel or CSV file"
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 14px', background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0', borderRadius: 10, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
              <Upload size={14} /> {importStatus.state === 'loading' ? 'Reading file...' : 'Import Excel / CSV'}
            </button>
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={handleImportFile} />
          </>}

          {/* Export Excel */}
          {perms.canExportExcel && (
          <button onClick={handleExport}
            title="Export full catalog to Excel"
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 14px', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 10, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
            <Download size={14} /> Export Excel
          </button>
          )}
{/* Print Barcodes */}
          {perms.canExportExcel && (
          <button onClick={openBarcodePicker}
            title="Print barcode labels for all products"
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 14px', background: '#fdf4ff', color: '#7e22ce', border: '1px solid #e9d5ff', borderRadius: 10, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
            🏷️ Print Barcodes
          </button>
          )}
          {perms.canExportExcel && (
          <button onClick={() => setShowPrintSettings(s => !s)} title="Printer Settings"
            style={{ background: showPrintSettings ? '#e0e7ff' : '#f8fafc', border: `1px solid ${showPrintSettings ? '#818cf8' : '#e2e8f0'}`, borderRadius: 10, padding: '9px 11px', fontSize: 13, cursor: 'pointer', color: showPrintSettings ? '#4f46e5' : '#475569' }}>
            ⚙
          </button>
          )}
          {/* Add Product */}
          {perms.canAddProduct && <button onClick={() => {
            const defB2CName = customTaxScheme?.name || defaultScheme.label.replace(/\s*\(.*?\)/, '');
            const defB2CRate = customTaxScheme?.components?.[0]?.rate ?? defaultScheme.common_rates?.[defaultScheme.common_rates.length - 1] ?? 0;
            const defB2BName = (customTaxScheme as any)?.b2b_name || '';
            const defB2BRate = (customTaxScheme as any)?.b2b_rate;

            setForm({
              ...empty(defaultScheme.id),
              tax_name: defB2CName,
              tax_rate: defB2CRate,
              b2b_tax_name: defB2BName,
              b2b_tax_rate: defB2BRate,
            });
            setShowAdd(true);
            setError('');
          }}
          
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
            <Plus size={16} /> Add Product
          </button>
          }
{perms.canAddProduct && (
<button onClick={() => setShowScanner(true)}
  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: '#fdf4ff', color: '#7e22ce', border: '1px solid #e9d5ff', borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
  📷 Scan & Add
</button>
)}
{perms.canAddProduct && (
<button onClick={() => setAddingColumn(a => !a)}
  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0', borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
  <Plus size={16} /> Add Column
</button>
)}
{hiddenKeys.length > 0 && (
<button
  onClick={() => setHiddenKeys([])}
  title="Show all hidden columns"
  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 14px', background: '#eff6ff', border: '1px solid #bfdbfe', color: '#2563eb', borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
  👁 Show All ({hiddenKeys.length})
</button>
)}
        </div>
      </div>



            {/* Outlet country & tax region — drives which tax scheme is offered below */}
      <div style={{
        display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16,
        padding: '14px 18px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
        boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 30, height: 30, borderRadius: 8, background: '#eef2ff',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <span style={{ fontSize: 14 }}>🌍</span>
          </div>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' as const, letterSpacing: '0.04em' }}>
            Business Country
          </span>
        </div>

        <select value={outletCountry} onChange={e => handleOutletCountryChange(e.target.value)}
          style={{
            padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 9, fontSize: 13,
            fontWeight: 600, color: outletCountry ? '#0f172a' : '#94a3b8', outline: 'none',
            background: '#f8fafc', cursor: 'pointer', minWidth: 180,
          }}>
          <option value="">Select country…</option>
          {getOrderedCountryList().map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
        </select>

        {(!!defaultScheme.region_based || REGIONAL_TAX_COUNTRIES.has(outletCountry)) && (
          <StatePicker
            countryCode={outletCountry}
            value={outletRegion}
            onChange={r => {
              handleOutletRegionChange(r);
            }}
            placeholder="State / Province"
          />
        )}

        <div style={{ width: 1, height: 22, background: '#e2e8f0' }} />

        {/* Scheme selector + breakdown chips matching mobile TaxSettings card */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f8fafc', padding: '4px 10px', borderRadius: 8, border: '1px solid #e2e8f0' }}>
          <span style={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Tax Scheme:</span>
          <select
            value={defaultScheme.id}
            onChange={async e => {
              const val = e.target.value;
              if (val === 'custom') {
                openCustomTaxModal();
              } else {
                setSelectedSchemeId(val);
                if (outletId) localStorage.setItem(`cloudairambo_outlet_scheme_${outletId}`, val);
                const jwt = localStorage.getItem('cloudairambo_jwt') || '';
                fetch(`${BACKEND}/outlets/${outletId}`, {
                  method: 'PATCH',
                  headers: { 'Content-Type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}) },
                  body: JSON.stringify({ tax_scheme_id: val }),
                }).catch(() => {});
              }
            }}
            style={{ padding: '4px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12, fontWeight: 700, color: '#1e293b', background: '#fff', cursor: 'pointer', outline: 'none' }}
          >
            {availableSchemes.map(sch => (
              <option key={sch.id} value={sch.id}>{sch.label}</option>
            ))}
          </select>
          <button onClick={openCustomTaxModal} title="Create or edit a custom named tax"
            style={{ background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 6, cursor: 'pointer', color: '#4f46e5', fontSize: 11, fontWeight: 700, padding: '3px 8px' }}>
            ✏️ Edit Tax
          </button>
        </div>

        {/* Itemized Components preview OR Single Flat Tax preview */}
        {defaultScheme.components && defaultScheme.components.length > 1 ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {defaultScheme.components.map((c, i) => (
              <span key={i} style={{ fontSize: 11, color: '#475569', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, padding: '2px 8px' }}>
                • {c.name}: <strong>{c.rate}%</strong>
              </span>
            ))}
          </div>
        ) : defaultScheme.components && defaultScheme.components.length === 1 ? (
          <span style={{ fontSize: 11, color: '#475569', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, padding: '2px 8px' }}>
            • {defaultScheme.components[0].name}: <strong>{defaultScheme.components[0].rate}%</strong>
          </span>
        ) : (defaultScheme.common_rates && defaultScheme.common_rates.length > 0) ? (
          <span style={{ fontSize: 11, color: '#64748b', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '2px 8px' }}>
            Flat Tax ({defaultScheme.common_rates.slice(-3).join('%, ')}%)
          </span>
        ) : null}

        {products.length > 0 && (
          <button onClick={() => { setBackfillScheme(defaultScheme.id); setBackfillTarget('all'); setShowTaxBackfill(true); }}
            style={{
              marginLeft: 'auto', padding: '7px 14px', background: '#4f46e5', color: '#fff',
              border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
              boxShadow: '0 1px 2px rgba(79,70,229,0.25)',
            }}>
            Apply to all products
          </button>
        )}
      </div>

    
      {/* Printer settings — same USB/Bluetooth options as Quick Invoice */}
      {showPrintSettings && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16, maxWidth: 420 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>Print via USB (skip dialog)</div>
              <div style={{ fontSize: 10, color: '#6b7280' }}>
                {usbConnected ? `Connected: ${getUSBPrinterName() || 'USB Printer'}` : 'No USB printer connected'}
              </div>
            </div>
            <button onClick={() => {
              const next = !useUSBPrinter;
              setUseUSBPrinter(next);
              localStorage.setItem('cloudairambo_catalog_usb_print', String(next));
              if (next) { setUseBTPrinter(false); localStorage.setItem('cloudairambo_catalog_bt_print', 'false'); }
            }} style={{
              width: 42, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
              background: useUSBPrinter ? '#6366f1' : '#d1d5db', position: 'relative', flexShrink: 0,
            }}>
              <div style={{ width: 18, height: 18, borderRadius: '50%', background: '#fff', position: 'absolute', top: 3, left: useUSBPrinter ? 21 : 3, transition: 'left 0.2s' }} />
            </button>
          </div>
          {useUSBPrinter && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: '#6b7280' }}>Roll width:</span>
              {[30, 40, 50, 58, 80].map(w => (
                <button key={w} onClick={() => {
                  setUsbRollWidthMm(w);
                  localStorage.setItem('cloudairambo_usb_roll_width_mm', String(w));
                }} style={{
                  fontSize: 10, fontWeight: 700, padding: '4px 8px', borderRadius: 6, cursor: 'pointer',
                  border: `1px solid ${usbRollWidthMm === w ? '#6366f1' : '#e2e8f0'}`,
                  background: usbRollWidthMm === w ? '#eef2ff' : '#fff',
                  color: usbRollWidthMm === w ? '#4f46e5' : '#6b7280',
                }}>
                  {w}mm
                </button>
              ))}
            </div>
          )}
          {useUSBPrinter && !usbConnected && (
            <button onClick={async () => { const ok = await connectUSBPrinter(); setUsbConnected(ok); }} style={{
              fontSize: 11, fontWeight: 700, padding: '6px 8px', borderRadius: 6,
              border: '1px solid #c7d2fe', background: '#eef2ff', color: '#4f46e5', cursor: 'pointer',
            }}>
              🔌 Connect USB Printer
            </button>
          )}
          {useUSBPrinter && usbConnected && (
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={async () => { const ok = await connectUSBPrinter(); setUsbConnected(ok); }} style={{
                flex: 1, fontSize: 11, fontWeight: 700, padding: '6px 8px', borderRadius: 6,
                border: '1px solid #c7d2fe', background: '#eef2ff', color: '#4f46e5', cursor: 'pointer',
              }}>
                🔁 Switch USB Printer
              </button>
              <button onClick={() => { disconnectUSBPrinter(); setUsbConnected(false); }} style={{
                fontSize: 11, fontWeight: 700, padding: '6px 10px', borderRadius: 6,
                border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626', cursor: 'pointer',
              }}>
                ✕ Disconnect
              </button>
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>Print via Bluetooth</div>
              <div style={{ fontSize: 10, color: '#6b7280' }}>
                {btConnected ? `Connected: ${getBTPrinterName() || 'BT Printer'}` : 'No Bluetooth printer connected'}
              </div>
            </div>
            <button onClick={() => {
              const next = !useBTPrinter;
              setUseBTPrinter(next);
              localStorage.setItem('cloudairambo_catalog_bt_print', String(next));
              if (next) { setUseUSBPrinter(false); localStorage.setItem('cloudairambo_catalog_usb_print', 'false'); }
            }} style={{
              width: 42, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
              background: useBTPrinter ? '#6366f1' : '#d1d5db', position: 'relative', flexShrink: 0,
            }}>
              <div style={{ width: 18, height: 18, borderRadius: '50%', background: '#fff', position: 'absolute', top: 3, left: useBTPrinter ? 21 : 3, transition: 'left 0.2s' }} />
            </button>
          </div>
          {useBTPrinter && (
            <>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: '#6b7280' }}>Printer type:</span>
                <button onClick={() => { setBtPrinterLang('zpl'); localStorage.setItem('cloudairambo_bt_printer_lang', 'zpl'); }}
                  style={{
                    fontSize: 10, fontWeight: 700, padding: '4px 8px', borderRadius: 6, cursor: 'pointer',
                    border: `1px solid ${btPrinterLang === 'zpl' ? '#6366f1' : '#e2e8f0'}`,
                    background: btPrinterLang === 'zpl' ? '#eef2ff' : '#fff',
                    color: btPrinterLang === 'zpl' ? '#4f46e5' : '#6b7280',
                  }}>
                  🏷️ Label (ZPL)
                </button>
                <button onClick={() => { setBtPrinterLang('escpos'); localStorage.setItem('cloudairambo_bt_printer_lang', 'escpos'); }}
                  style={{
                    fontSize: 10, fontWeight: 700, padding: '4px 8px', borderRadius: 6, cursor: 'pointer',
                    border: `1px solid ${btPrinterLang === 'escpos' ? '#6366f1' : '#e2e8f0'}`,
                    background: btPrinterLang === 'escpos' ? '#eef2ff' : '#fff',
                    color: btPrinterLang === 'escpos' ? '#4f46e5' : '#6b7280',
                  }}>
                  🧾 Receipt (ESC/POS)
                </button>
              </div>
              <button onClick={async () => { const char = await connectBluetoothPrinter(); setBtConnected(!!char); }} style={{
                fontSize: 11, fontWeight: 700, padding: '6px 8px', borderRadius: 6,
                border: '1px solid #c7d2fe', background: '#eef2ff', color: '#4f46e5', cursor: 'pointer',
              }}>
                🔵 Connect Bluetooth Printer
              </button>
            </>
          )}
        </div>
      )}

      {/* Stats strip */}
      <div style={{ display: 'flex', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', marginBottom: 16, background: '#fff' }}>
        <div style={{ flex: 1, textAlign: 'center', padding: '12px 8px', borderRight: '1px solid #f1f5f9' }}>
          <div style={{ fontSize: 20, fontWeight: 900, color: '#6366f1' }}>{products.length}</div>
          <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Total</div>
        </div>
        <div style={{ flex: 1, textAlign: 'center', padding: '12px 8px', borderRight: '1px solid #f1f5f9' }}>
          <div style={{ fontSize: 20, fontWeight: 900, color: '#f59e0b' }}>{lowStockCount}</div>
          <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Low Stock</div>
        </div>
        <div style={{ flex: 1, textAlign: 'center', padding: '12px 8px', borderRight: '1px solid #f1f5f9' }}>
          <div style={{ fontSize: 20, fontWeight: 900, color: '#ef4444' }}>{outOfStockCount}</div>
          <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Out of Stock</div>
        </div>
        <div style={{ flex: 1, textAlign: 'center', padding: '12px 8px' }}>
          <div style={{ fontSize: 20, fontWeight: 900, color: '#16a34a' }}>{healthyCount}</div>
          <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Healthy</div>
        </div>
      </div>

      {/* Low stock warning banner */}
      {(lowStockCount > 0 || outOfStockCount > 0) && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10,
          padding: '10px 14px', marginBottom: 16, fontSize: 13, fontWeight: 700, color: '#b45309',
        }}>
          ⚠️ {outOfStockCount > 0 ? `${outOfStockCount} out of stock · ` : ''}{lowStockCount} below minimum stock
        </div>
      )}

      {/* Legacy tax data — products saved before tax schemes existed */}
      {legacyTaxCount > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8,
          background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10,
          padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#1e3a8a',
        }}>
          <span>ℹ️ {legacyTaxCount} product{legacyTaxCount !== 1 ? 's' : ''} {legacyTaxCount !== 1 ? "don't" : "doesn't"} have a tax scheme assigned yet — the CGST/SGST-style breakdown won't show for {legacyTaxCount !== 1 ? 'them' : 'it'} until you assign one.</span>
          <button onClick={() => { setBackfillScheme(defaultScheme.id); setBackfillTarget('missing'); setShowTaxBackfill(true); }}
            style={{ padding: '6px 14px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            Assign Tax Scheme
          </button>
        </div>
      )}

      {/* Import result banner */}
      {importStatus.state === 'done' && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px', borderRadius: 10, marginBottom: 16,
          background: importStatus.message ? '#fef2f2' : '#f0fdf4',
          border: `1px solid ${importStatus.message ? '#fecaca' : '#bbf7d0'}`,
          color: importStatus.message ? '#991b1b' : '#166534',
          fontSize: 13, fontWeight: 600,
        }}>
          <span>
            {importStatus.message
              ? `⚠️ ${importStatus.message}`
              : `✅ Import complete — ${importStatus.inserted} added${importStatus.updated ? `, ${importStatus.updated} updated` : ''}${importStatus.skipped ? `, ${importStatus.skipped} skipped` : ''}`}
          </span>
          <button onClick={() => setImportStatus({ state: 'idle' })}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', marginLeft: 12 }}>
            <X size={14} />
          </button>
        </div>
      )}

      {/* Import preview — review parsed rows before they're saved */}
      {importPreview && (
        <div
          onClick={handleCancelImport}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)',
            backdropFilter: 'blur(2px)', zIndex: 100,
            display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
            padding: '5vh 16px', overflowY: 'auto',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, padding: 20,
              maxWidth: '95vw', width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,0.25)',
              maxHeight: '92vh', overflowY: 'auto', boxSizing: 'border-box',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>Review Import — {importPreview.fileName}</div>
              <button onClick={handleCancelImport}
                style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, width: 28, height: 28, cursor: 'pointer', color: '#64748b', fontSize: 14, fontWeight: 700 }}>
                ✕
              </button>
            </div>
            <p style={{ margin: '4px 0 14px', fontSize: 12, color: '#64748b' }}>
              {importPreview.rows.length} product{importPreview.rows.length !== 1 ? 's' : ''} detected
              {importPreview.customCols.length > 0 && <> · {importPreview.customCols.length} extra column{importPreview.customCols.length !== 1 ? 's' : ''} found and will be added as custom fields: <strong>{importPreview.customCols.join(', ')}</strong></>}
              . Remove any rows that look wrong, then confirm to save.
            </p>

            <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
              <div className="import-preview-scroll" style={{ overflow: 'auto', maxHeight: '62vh' }}>
                <div style={{ minWidth: 780 + importPreview.customCols.length * 110 }}>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: `130px 1fr 100px 90px 80px 70px 65px 65px ${importPreview.customCols.map(() => '110px').join(' ')} 40px`,
                    background: '#f8fafc', borderBottom: '1px solid #e2e8f0', padding: '8px 12px',
                    fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase',
                  }}>
                    <span style={{ textAlign: 'center' }}>Barcode</span>
                    <span>Name</span>
                    <span>Brand</span>
                    <span>Category</span>
                    <span style={{ textAlign: 'center' }}>Rate</span>
                    <span style={{ textAlign: 'center' }}>Tax %</span>
                    <span style={{ textAlign: 'center' }}>Stock</span>
                    <span style={{ textAlign: 'center' }}>Unit</span>
                    {importPreview.customCols.map(c => <span key={c} style={{ textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={c}>{c}</span>)}
                    <span></span>
                  </div>
                  {importPreview.rows.map((r, i) => (
                    <div key={i} style={{
                      display: 'grid',
                      gridTemplateColumns: `130px 1fr 100px 90px 80px 70px 65px 65px ${importPreview.customCols.map(() => '110px').join(' ')} 40px`,
                      padding: '8px 12px', borderBottom: '1px solid #f1f5f9', alignItems: 'center', fontSize: 12,
                    }}>
                      <span style={{ fontFamily: 'monospace', fontSize: 10, color: '#475569' }}>{r.barcode || <span style={{ color: '#10b981', fontStyle: 'italic' }}>Auto</span>}</span>
                      <span title={r.name} style={{ fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</span>
                      <span title={r.brand} style={{ fontSize: 11, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.brand || '—'}</span>
                      <span style={{ color: '#64748b' }}>{r.category || '—'}</span>
                      <span style={{ fontWeight: 700, color: '#0f172a', textAlign: 'center' }}>{r.rate > 0 ? `${currSym}${r.rate}` : '—'}</span>
                      <span style={{ fontSize: 11, color: '#64748b', textAlign: 'center' }}>{r.tax_rate ? `${r.tax_rate}%` : '0%'}</span>
                      <span style={{ fontWeight: 600, color: '#0f172a', textAlign: 'center' }}>{r.stock_quantity ?? 0}</span>
                      <span style={{ color: '#64748b', textAlign: 'center' }}>{r.unit || '—'}</span>
                      {importPreview.customCols.map(c => (
                        <span key={c} style={{ color: '#7c3aed', fontSize: 11, textAlign: 'center' }}>{r.custom_fields?.[c] || '—'}</span>
                      ))}
                      <button onClick={() => handleRemovePreviewRow(i)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444' }}>
                        <X size={13} />
                      </button>
                    </div>
                  ))}
                  {importPreview.rows.length === 0 && (
                    <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>
                      All rows removed — nothing left to import.
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button onClick={handleConfirmImport} disabled={importSaving || importPreview.rows.length === 0}
                style={{ padding: '9px 20px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: (importSaving || importPreview.rows.length === 0) ? 0.6 : 1 }}>
                {importSaving ? 'Saving...' : `Confirm & Save ${importPreview.rows.length}`}
              </button>
              <button onClick={handleCancelImport}
                style={{ padding: '9px 16px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
{showTaxBackfill && (() => {
  const targetCount = backfillTarget === 'all' ? products.length : legacyTaxCount;
  return (
    <div onClick={() => setShowTaxBackfill(false)} style={{
      position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(2px)', zIndex: 100,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, padding: 22, maxWidth: 440, width: '100%',
        boxShadow: '0 24px 64px rgba(0,0,0,0.25)',
      }}>
        <div style={{ fontWeight: 800, fontSize: 15, color: '#0f172a', marginBottom: 8 }}>Apply Tax to Catalog Products</div>

        {/* 1. Target Scope */}
        <label style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Apply To Products</label>
        <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
          <button onClick={() => setBackfillTarget('all')}
            style={{
              flex: 1, padding: '7px 10px', borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: 'pointer',
              border: `1px solid ${backfillTarget === 'all' ? '#6366f1' : '#e2e8f0'}`,
              background: backfillTarget === 'all' ? '#eef2ff' : '#fff',
              color: backfillTarget === 'all' ? '#4f46e5' : '#64748b',
            }}>
            ALL Products ({products.length})
          </button>
          <button onClick={() => setBackfillTarget('missing')}
            style={{
              flex: 1, padding: '7px 10px', borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: 'pointer',
              border: `1px solid ${backfillTarget === 'missing' ? '#6366f1' : '#e2e8f0'}`,
              background: backfillTarget === 'missing' ? '#eef2ff' : '#fff',
              color: backfillTarget === 'missing' ? '#4f46e5' : '#64748b',
            }}>
            Only Missing ({legacyTaxCount})
          </button>
        </div>

        {/* 2. Tax Tier Scope: B2C, B2B, or Both */}
        <label style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Which Tax to Update?</label>
        <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
          <button onClick={() => setBackfillScope('both')}
            style={{
              flex: 1, padding: '7px 6px', borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: 'pointer',
              border: `1.5px solid ${backfillScope === 'both' ? '#6366f1' : '#e2e8f0'}`,
              background: backfillScope === 'both' ? '#eef2ff' : '#fff',
              color: backfillScope === 'both' ? '#4f46e5' : '#64748b',
            }}>
            👥 Both (B2C & B2B)
          </button>
          <button onClick={() => setBackfillScope('b2c')}
            style={{
              flex: 1, padding: '7px 6px', borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: 'pointer',
              border: `1.5px solid ${backfillScope === 'b2c' ? '#6366f1' : '#e2e8f0'}`,
              background: backfillScope === 'b2c' ? '#eef2ff' : '#fff',
              color: backfillScope === 'b2c' ? '#4f46e5' : '#64748b',
            }}>
            👤 B2C Only
          </button>
          <button onClick={() => setBackfillScope('b2b')}
            style={{
              flex: 1, padding: '7px 6px', borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: 'pointer',
              border: `1.5px solid ${backfillScope === 'b2b' ? '#16a34a' : '#e2e8f0'}`,
              background: backfillScope === 'b2b' ? '#f0fdf4' : '#fff',
              color: backfillScope === 'b2b' ? '#15803d' : '#64748b',
            }}>
            🏢 B2B Only
          </button>
        </div>

        {/* 3. Scheme Selector */}
        <label style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Select Scheme to Apply</label>
        <select value={backfillScheme} onChange={e => setBackfillScheme(e.target.value)}
          style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 12, fontWeight: 600, outline: 'none', marginBottom: 16, boxSizing: 'border-box' as const }}>
          {availableSchemes.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>

        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={handleBackfillTaxScheme} disabled={backfillBusy || targetCount === 0}
            style={{ padding: '9px 20px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: (backfillBusy || targetCount === 0) ? 0.6 : 1 }}>
            {backfillBusy ? 'Applying...' : `Apply to ${targetCount} Products`}
          </button>
          <button onClick={() => setShowTaxBackfill(false)}
            style={{ padding: '9px 16px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
})()}

{showCustomTaxModal && (
  <div onClick={() => setShowCustomTaxModal(false)} style={{
    position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(2px)', zIndex: 100,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
  }}>
    <div onClick={e => e.stopPropagation()} style={{
      background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, padding: 22, maxWidth: 520, width: '100%',
      boxShadow: '0 24px 64px rgba(0,0,0,0.25)', maxHeight: '90vh', overflowY: 'auto',
    }}>
      <div style={{ fontWeight: 800, fontSize: 15, color: '#0f172a', marginBottom: 4 }}>✏️ Configure Business Tax</div>
      <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 14px' }}>
        Configure your store's default B2C consumer tax and optional B2B corporate tax.
      </p>

      {/* 👤 B2C Section */}
      <div style={{ background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: 12, padding: 14, marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: '#4f46e5', marginBottom: 10 }}>
          👤 B2C Retail Tax (Consumer / Walk-in)
        </div>

        {/* B2C Tax Mode Toggle */}
        <div style={{ display: 'flex', background: '#e2e8f0', padding: 3, borderRadius: 8, marginBottom: 10 }}>
          <button type="button" onClick={() => setCustomTaxMode('single')}
            style={{
              flex: 1, padding: '6px 0', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer',
              background: customTaxMode === 'single' ? '#fff' : 'transparent',
              color: customTaxMode === 'single' ? '#0f172a' : '#64748b',
              boxShadow: customTaxMode === 'single' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
            }}>
            Single Flat Tax (VAT / Sales Tax)
          </button>
          <button type="button" onClick={() => setCustomTaxMode('split')}
            style={{
              flex: 1, padding: '6px 0', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer',
              background: customTaxMode === 'split' ? '#fff' : 'transparent',
              color: customTaxMode === 'split' ? '#0f172a' : '#64748b',
              boxShadow: customTaxMode === 'split' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
            }}>
            Split Tax (CGST + SGST)
          </button>
        </div>

        <label style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', display: 'block', marginBottom: 3 }}>B2C Tax Name</label>
        <input type="text" autoFocus value={customTaxModalName} onChange={e => setCustomTaxModalName(e.target.value)}
          placeholder="e.g. VAT, Retail GST, Sales Tax"
          style={{ width: '100%', padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 7, fontSize: 12, outline: 'none', marginBottom: 10, background: '#fff', boxSizing: 'border-box' as const }} />

        {customTaxMode === 'single' ? (
          <div>
            <label style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', display: 'block', marginBottom: 3 }}>Standard Rate (%)</label>
            <input type="number" step="0.01" value={customTaxFlatRate || ''} placeholder="e.g. 5, 15, 20"
              onChange={e => setCustomTaxFlatRate(parseFloat(e.target.value) || 0)}
              style={{ width: '100%', padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 7, fontSize: 12, outline: 'none', background: '#fff', boxSizing: 'border-box' as const }} />
          </div>
        ) : (
          <div>
            <label style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Components</label>
            {customTaxModalComponents.map((c, i) => (
              <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 8, alignItems: 'center' }}>
                <input type="text" value={c.name} placeholder="e.g. CGST"
                  onChange={e => setCustomTaxModalComponents(rows => rows.map((r, j) => j === i ? { ...r, name: e.target.value } : r))}
                  style={{ flex: 1, padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 7, fontSize: 12, outline: 'none', background: '#fff', boxSizing: 'border-box' as const }} />
                <input type="number" step="0.01" value={c.rate || ''} placeholder="Rate %"
                  onChange={e => setCustomTaxModalComponents(rows => rows.map((r, j) => j === i ? { ...r, rate: parseFloat(e.target.value) || 0 } : r))}
                  style={{ width: 80, padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 7, fontSize: 12, outline: 'none', background: '#fff', boxSizing: 'border-box' as const }} />
                <button onClick={() => setCustomTaxModalComponents(rows => rows.length > 1 ? rows.filter((_, j) => j !== i) : rows)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 4 }}>
                  <X size={14} />
                </button>
              </div>
            ))}
            <button onClick={() => setCustomTaxModalComponents(rows => [...rows, { name: '', rate: 0 }])}
              style={{ padding: '5px 10px', background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer', color: '#475569' }}>
              + Add Component
            </button>
          </div>
        )}
      </div>

      {/* 🏢 B2B Section (Exact Same Design and Power as B2C) */}
      <div style={{ background: customB2BEnabled ? '#f0fdf4' : '#f8fafc', border: `1.5px solid ${customB2BEnabled ? '#86efac' : '#e2e8f0'}`, borderRadius: 12, padding: 14, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: customB2BEnabled ? 10 : 0 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 800, color: customB2BEnabled ? '#16a34a' : '#475569' }}>
              🏢 B2B Business Tax (When Buyer Has Tax ID)
            </div>
            <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>
              {customB2BEnabled ? 'Custom corporate tax active' : 'Currently inherits same rate and split as B2C'}
            </div>
          </div>
          <button type="button" onClick={() => setCustomB2BEnabled(e => !e)}
            style={{
              padding: '5px 10px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700,
              background: customB2BEnabled ? '#16a34a' : '#e2e8f0', color: customB2BEnabled ? '#fff' : '#475569',
            }}>
            {customB2BEnabled ? '✓ Custom B2B Active' : '+ Customize B2B'}
          </button>
        </div>

        {customB2BEnabled && (
          <div style={{ marginTop: 10 }}>
            {/* B2B Tax Mode Toggle (Same as B2C!) */}
            <div style={{ display: 'flex', background: '#dcfce7', padding: 3, borderRadius: 8, marginBottom: 10 }}>
              <button type="button" onClick={() => setCustomB2BMode('single')}
                style={{
                  flex: 1, padding: '6px 0', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                  background: customB2BMode === 'single' ? '#fff' : 'transparent',
                  color: customB2BMode === 'single' ? '#166534' : '#64748b',
                  boxShadow: customB2BMode === 'single' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                }}>
                Single Flat Tax (e.g. 18%)
              </button>
              <button type="button" onClick={() => setCustomB2BMode('split')}
                style={{
                  flex: 1, padding: '6px 0', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                  background: customB2BMode === 'split' ? '#fff' : 'transparent',
                  color: customB2BMode === 'split' ? '#166534' : '#64748b',
                  boxShadow: customB2BMode === 'split' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                }}>
                Split Tax (Components)
              </button>
            </div>

            <label style={{ fontSize: 10, fontWeight: 700, color: '#166534', textTransform: 'uppercase', display: 'block', marginBottom: 3 }}>B2B Tax Name</label>
            <input type="text" value={customTaxModalB2BName} onChange={e => setCustomTaxModalB2BName(e.target.value)}
              placeholder={customTaxModalName ? `${customTaxModalName} B2B` : 'e.g. Corporate Tax, B2B GST'}
              style={{ width: '100%', padding: '7px 10px', border: '1px solid #86efac', borderRadius: 7, fontSize: 12, outline: 'none', marginBottom: 10, background: '#fff', boxSizing: 'border-box' as const }} />

            {customB2BMode === 'single' ? (
              <div>
                <label style={{ fontSize: 10, fontWeight: 700, color: '#166534', textTransform: 'uppercase', display: 'block', marginBottom: 3 }}>B2B Rate (%)</label>
                <input type="number" step="0.01" value={customTaxModalB2BFlatRate || ''} placeholder="e.g. 18"
                  onChange={e => setCustomTaxModalB2BFlatRate(parseFloat(e.target.value) || 0)}
                  style={{ width: '100%', padding: '7px 10px', border: '1px solid #86efac', borderRadius: 7, fontSize: 12, fontWeight: 700, color: '#16a34a', outline: 'none', background: '#fff', boxSizing: 'border-box' as const }} />
              </div>
            ) : (
              <div>
                <label style={{ fontSize: 10, fontWeight: 700, color: '#166534', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>B2B Components</label>
                {customTaxModalB2BComponents.map((c, i) => (
                  <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 8, alignItems: 'center' }}>
                    <input type="text" value={c.name} placeholder="e.g. CGST (B2B)"
                      onChange={e => setCustomTaxModalB2BComponents(rows => rows.map((r, j) => j === i ? { ...r, name: e.target.value } : r))}
                      style={{ flex: 1, padding: '7px 10px', border: '1px solid #86efac', borderRadius: 7, fontSize: 12, outline: 'none', background: '#fff', boxSizing: 'border-box' as const }} />
                    <input type="number" step="0.01" value={c.rate || ''} placeholder="Rate %"
                      onChange={e => setCustomTaxModalB2BComponents(rows => rows.map((r, j) => j === i ? { ...r, rate: parseFloat(e.target.value) || 0 } : r))}
                      style={{ width: 80, padding: '7px 10px', border: '1px solid #86efac', borderRadius: 7, fontSize: 12, fontWeight: 700, color: '#16a34a', outline: 'none', background: '#fff', boxSizing: 'border-box' as const }} />
                    <button onClick={() => setCustomTaxModalB2BComponents(rows => rows.length > 1 ? rows.filter((_, j) => j !== i) : rows)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 4 }}>
                      <X size={14} />
                    </button>
                  </div>
                ))}
                <button onClick={() => setCustomTaxModalB2BComponents(rows => [...rows, { name: '', rate: 0 }])}
                  style={{ padding: '5px 10px', background: '#fff', border: '1px solid #86efac', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer', color: '#166534' }}>
                  + Add B2B Component
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={handleSaveCustomTaxModal} disabled={customTaxSaving || !customTaxModalName.trim()}
          style={{ padding: '9px 20px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: (customTaxSaving || !customTaxModalName.trim()) ? 0.6 : 1 }}>
          {customTaxSaving ? 'Saving...' : 'Save Business Tax'}
        </button>
        <button onClick={() => setShowCustomTaxModal(false)}
          style={{ padding: '9px 16px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
          Cancel
        </button>
      </div>
    </div>
  </div>
)}

{showBarcodePicker && (
  <div
    onClick={() => setShowBarcodePicker(false)}
    style={{
      position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)',
      backdropFilter: 'blur(2px)', zIndex: 100,
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      padding: '5vh 16px', overflowY: 'auto',
    }}
  >
    <div
      onClick={e => e.stopPropagation()}
      style={{
        background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, padding: 20,
        maxWidth: 720, width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,0.25)',
        maxHeight: '92vh', overflowY: 'auto', boxSizing: 'border-box',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>🏷️ Print Barcode Labels</div>
        <button onClick={() => setShowBarcodePicker(false)}
          style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, width: 28, height: 28, cursor: 'pointer', color: '#64748b', fontSize: 14, fontWeight: 700 }}>
          ✕
        </button>
      </div>

      {/* Label size + select all/none */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', margin: '12px 0' }}>
        <select value={pickerPreset} onChange={e => setPickerPreset(e.target.value as keyof typeof LABEL_PRESETS)}
          style={{ padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12, outline: 'none' }}>
          {Object.entries(LABEL_PRESETS).map(([key, p]) => (
            <option key={key} value={key}>{p.name}</option>
          ))}
        </select>
        <button onClick={() => setPickerSelected(new Set((filtered.length > 0 ? filtered : products).map(p => p.id)))}
          style={{ padding: '7px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
          Select All
        </button>
        <button onClick={() => setPickerSelected(new Set())}
          style={{ padding: '7px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
          Deselect All
        </button>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: '#64748b', fontWeight: 600 }}>
          {pickerSelected.size} product{pickerSelected.size !== 1 ? 's' : ''} · {pickerTotalLabels()} label{pickerTotalLabels() !== 1 ? 's' : ''} total
        </span>
      </div>

      {/* Which fields to include on the label */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 10, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
          Show on label
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {([
            { key: 'name', label: 'Name' },
            { key: 'brand', label: 'Brand' },
            { key: 'barcodeNumber', label: 'Barcode #' },
            { key: 'price', label: 'MRP' },
            { key: 'unit', label: 'Unit' },
          ] as const).map(f => {
            const active = labelFields[f.key];
            return (
              <button key={f.key} type="button"
                onClick={() => setLabelFields(prev => ({ ...prev, [f.key]: !prev[f.key] }))}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5, padding: '6px 10px', borderRadius: 8, cursor: 'pointer',
                  border: `1px solid ${active ? '#6366f1' : '#e2e8f0'}`,
                  background: active ? '#eef2ff' : '#f8fafc',
                  fontSize: 11, fontWeight: 700, color: active ? '#4f46e5' : '#64748b',
                }}>
                <span style={{
                  width: 14, height: 14, borderRadius: 3, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  border: `1.5px solid ${active ? '#6366f1' : '#cbd5e1'}`, background: active ? '#6366f1' : '#fff',
                }}>
                  {active && <span style={{ color: '#fff', fontSize: 9, fontWeight: 900 }}>✓</span>}
                </span>
                {f.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Product list */}
      <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, maxHeight: '48vh', overflowY: 'auto' }}>
        {(filtered.length > 0 ? filtered : products).map(p => {
          const checked = pickerSelected.has(p.id);
          const img = barcodeDataUrl(p.barcode);
          return (
            <div key={p.id} onClick={() => togglePickerSelect(p.id)} style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
              borderBottom: '1px solid #f1f5f9', cursor: 'pointer',
              opacity: checked ? 1 : 0.45, background: '#fff',
            }}>
              <input type="checkbox" checked={checked} onChange={() => togglePickerSelect(p.id)}
                onClick={e => e.stopPropagation()} style={{ width: 16, height: 16, cursor: 'pointer', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
                <div style={{ fontSize: 10, color: '#94a3b8', fontFamily: 'monospace' }}>{p.barcode}</div>
              </div>
              {img ? <img src={img} style={{ height: 32, flexShrink: 0 }} /> : <span style={{ fontSize: 10, color: '#ef4444', flexShrink: 0 }}>Invalid barcode</span>}
              <div onClick={e => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                <button onClick={() => setPickerQtyFor(p.id, (pickerQty[p.id] ?? 1) - 1)}
                  style={{ width: 22, height: 22, borderRadius: 6, border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer', fontSize: 13 }}>−</button>
                <input type="number" value={pickerQty[p.id] ?? 1} min={1} max={999}
                  onChange={e => setPickerQtyFor(p.id, parseInt(e.target.value))}
                  style={{ width: 40, textAlign: 'center', fontSize: 12, fontWeight: 700, padding: 4, border: '1px solid #e2e8f0', borderRadius: 6 }} />
                <button onClick={() => setPickerQtyFor(p.id, (pickerQty[p.id] ?? 1) + 1)}
                  style={{ width: 22, height: 22, borderRadius: 6, border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer', fontSize: 13 }}>+</button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
        <button onClick={handlePickerPrintPDF} disabled={pdfBusy || pickerSelected.size === 0}
          style={{ padding: '9px 18px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: pdfBusy || pickerSelected.size === 0 ? 0.6 : 1 }}>
          🖨️ Print via Browser
        </button>
        <button onClick={handlePickerDownloadPDF} disabled={pdfBusy || pickerSelected.size === 0}
          style={{ padding: '9px 18px', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: pdfBusy || pickerSelected.size === 0 ? 0.6 : 1 }}>
          ⬇ Download PDF
        </button>
        <button onClick={handlePickerDownloadHTML} disabled={pickerSelected.size === 0}
  style={{ padding: '9px 18px', background: '#fefce8', color: '#a16207', border: '1px solid #fde68a', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: pickerSelected.size === 0 ? 0.6 : 1 }}>
  ⬇ Download HTML
</button>
        {useUSBPrinter && usbConnected && (
          <button onClick={handlePickerPrintUSB} disabled={pickerSelected.size === 0}
            style={{ padding: '9px 18px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: pickerSelected.size === 0 ? 0.6 : 1 }}>
            🔌 Print via USB
          </button>
        )}
        {useUSBPrinter && !usbConnected && (
          <button onClick={handlePickerConnectAndPrintUSB} disabled={pickerSelected.size === 0 || usbConnecting}
            style={{ padding: '9px 18px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: (pickerSelected.size === 0 || usbConnecting) ? 0.6 : 1 }}>
            🔌 {usbConnecting ? 'Connecting...' : 'Connect & Print via USB'}
          </button>
        )}
        {useBTPrinter && btConnected && (
          <button onClick={handlePickerPrintBT} disabled={pickerSelected.size === 0}
            style={{ padding: '9px 18px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: pickerSelected.size === 0 ? 0.6 : 1 }}>
            🔵 Print via Bluetooth
          </button>
        )}
        {useBTPrinter && !btConnected && (
          <button onClick={handlePickerConnectAndPrintBT} disabled={pickerSelected.size === 0 || btConnecting}
            style={{ padding: '9px 18px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: (pickerSelected.size === 0 || btConnecting) ? 0.6 : 1 }}>
            🔵 {btConnecting ? 'Connecting...' : 'Connect & Print via Bluetooth'}
          </button>
        )}
        <button onClick={() => setShowBarcodePicker(false)}
          style={{ padding: '9px 16px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer', marginLeft: 'auto' }}>
          Cancel
        </button>
      </div>
    </div>
  </div>
)}

{addingColumn && (
  <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
    <input
      autoFocus
      type="text"
      placeholder="Column name (e.g. Expiry, HSN Code, Shelf...)"
      value={newColumnName}
      onChange={e => setNewColumnName(e.target.value)}
      onKeyDown={e => { if (e.key === 'Enter') handleAddColumn(); if (e.key === 'Escape') setAddingColumn(false); }}
      style={{ flex: 1, padding: '9px 14px', border: '1px solid #6366f1', borderRadius: 10, fontSize: 13, outline: 'none' }}
    />
    <button onClick={handleAddColumn}
      style={{ padding: '9px 20px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
      Add
    </button>
    <button onClick={() => setAddingColumn(false)}
      style={{ padding: '9px 14px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 10, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
      Cancel
    </button>
  </div>
)}
      {/* Search */}
      <div style={{ position: 'relative', marginBottom: 20 }}>
        <Search size={15} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
        <input type="text" placeholder="Search by name, barcode, brand..." value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ width: '100%', paddingLeft: 36, paddingRight: 12, paddingTop: 10, paddingBottom: 10, border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
      </div>

      {/* Add form — modal */}
     {perms.canAddProduct && showAdd && (
  <div
    onClick={() => { setShowAdd(false); setForm(empty()); setError(''); setCategoryCustom(false); setUnitCustom(false); setCustomFields([]); setNewFieldLabel(''); setProductQueue([]); }}
    style={{
      position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)',
      backdropFilter: 'blur(2px)', zIndex: 100,
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      padding: '5vh 16px', overflowY: 'auto',
    }}
  >
  <div
    onClick={e => e.stopPropagation()}
    style={{
      background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, padding: 20,
      maxWidth: 900, width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,0.25)',
      maxHeight: '90vh', overflowY: 'auto', boxSizing: 'border-box',
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
      <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>New Products</div>
      <button
        onClick={() => { setShowAdd(false); setForm(empty()); setError(''); setCategoryCustom(false); setUnitCustom(false); setCustomFields([]); setNewFieldLabel(''); setProductQueue([]); }}
        style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, width: 28, height: 28, cursor: 'pointer', color: '#64748b', fontSize: 14, fontWeight: 700 }}
      >
        ✕
      </button>
    </div>
    {error && <div style={{ color: '#ef4444', fontSize: 12, marginBottom: 12, fontWeight: 600 }}>{error}</div>}

    {/* Queue of products to be saved */}
    <div style={{ marginBottom: 16 }}>
  <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 8 }}>
    Queue — {productQueue.length} product{productQueue.length > 1 ? 's' : ''} ready to save
  </div>
  <div style={{ maxHeight: 180, overflowY: 'auto', paddingRight: 4, minHeight: 48, background: '#f1f5f9', borderRadius: 8, border: '1px dashed #cbd5e1', padding: '8px' }}>
    {productQueue.length === 0 && (
      <div style={{ color: '#94a3b8', fontSize: 12, textAlign: 'center', padding: '8px 0' }}>
        No products queued yet — fill the form and click <strong>+ Add Another</strong>
      </div>
    )}
        {productQueue.map((qp, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, marginBottom: 6, fontSize: 13 }}>
            <span style={{ flex: 1, fontWeight: 600, color: '#0f172a' }}>{qp.name}</span>
            {qp.brand && <span style={{ fontSize: 11, color: '#94a3b8' }}>{qp.brand}</span>}
            {qp.category && <span style={{ fontSize: 11, color: '#94a3b8' }}>{qp.category}</span>}
            <span style={{ fontSize: 12, fontWeight: 700, color: '#2563eb' }}>{qp.rate > 0 ? `${currSym}${qp.rate}` : ''}</span>
            {qp.unit && <span style={{ fontSize: 11, color: '#94a3b8' }}>{qp.unit}</span>}
            {Object.entries(qp.custom_fields || {}).map(([k, v]) => (
              <span key={k} style={{ fontSize: 11, color: '#7c3aed', background: '#f5f3ff', borderRadius: 4, padding: '1px 6px' }}>{k}: {v}</span>
            ))}
            <button onClick={() => setProductQueue(q => q.filter((_, j) => j !== i))}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 2 }}>
              <X size={13} />
            </button>
          </div>
        ))}
      </div>
    </div>



    {/* Form for one product */}
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
      <div style={{ gridColumn: 'span 2' }}>
  <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Barcode</label>
  <div style={{ display: 'flex', gap: 5 }}>
    <input type="text" value={form.barcode} onChange={e => setForm(f => ({ ...f, barcode: e.target.value }))}
      placeholder="Scan, type, or auto-generate"
      style={{ flex: 1, padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box' as const }} />
    <button type="button" onClick={async () => {
        const bc = await generateEAN13();
        setForm(f => ({ ...f, barcode: bc }));
      }}
      style={{ padding: '7px 10px', background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', borderRadius: 8, fontWeight: 700, fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap' as const }}>
      ⚡ EAN-13
    </button>
  </div>
</div>

      {[
        { label: 'Name *', key: 'name', type: 'text' },
        { label: 'Brand', key: 'brand', type: 'text' },
        { label: 'Rate', key: 'rate', type: 'number' },
      ].map(({ label, key, type }) => (
        <div key={key}>
          <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>{label}</label>
          <input type={type} value={type === 'number' ? ((form as any)[key] || '') : (form as any)[key]}
            onChange={e => setForm(f => ({ ...f, [key]: type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value }))}
            style={{ width: '100%', padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
        </div>
      ))}

      <div style={{ gridColumn: 'span 2', background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: 12, padding: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <span style={{ fontSize: 12, fontWeight: 800, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Tax Configuration (B2C & B2B)
          </span>
          <select value={form.tax_scheme_id || defaultScheme.id}
            onChange={e => {
              const scheme = getSchemeById(e.target.value);
              setForm(f => ({
                ...f,
                tax_scheme_id: e.target.value,
                tax_rate: (f.tax_rate && f.tax_rate > 0) ? f.tax_rate : (scheme?.common_rates?.[scheme.common_rates.length - 1] ?? 0),
              }));
            }}
            style={{ padding: '4px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 11, fontWeight: 700, color: '#1e293b', background: '#fff', cursor: 'pointer', outline: 'none' }}>
            {availableSchemes.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          {/* 👤 B2C Section */}
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: '#4f46e5', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
              👤 B2C Retail Tax (Standard)
            </div>
            <label style={{ fontSize: 10, fontWeight: 700, color: '#64748b', display: 'block', marginBottom: 3 }}>TAX NAME</label>
            <input type="text" value={form.tax_name || ''}
              placeholder="e.g. VAT, GST, Sales Tax"
              onChange={e => setForm(f => ({ ...f, tax_name: e.target.value }))}
              style={{ width: '100%', padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12, outline: 'none', marginBottom: 8, boxSizing: 'border-box' as const }} />

            <label style={{ fontSize: 10, fontWeight: 700, color: '#64748b', display: 'block', marginBottom: 3 }}>TAX RATE (%)</label>
            <input type="number" step="0.01" value={form.tax_rate ?? ''} placeholder="e.g. 5, 18"
              onChange={e => setForm(f => ({ ...f, tax_rate: parseFloat(e.target.value) || 0 }))}
              style={{ width: '100%', padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12, fontWeight: 700, outline: 'none', boxSizing: 'border-box' as const }} />

            <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', marginTop: 6 }}>
              {[0, 5, 12, 18, 28].map(r => (
                <button key={r} type="button" onClick={() => setForm(f => ({ ...f, tax_rate: r }))}
                  style={{
                    padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700, cursor: 'pointer',
                    border: `1px solid ${form.tax_rate === r ? '#6366f1' : '#e2e8f0'}`,
                    background: form.tax_rate === r ? '#eef2ff' : '#f8fafc',
                    color: form.tax_rate === r ? '#4f46e5' : '#64748b',
                  }}>{r}%</button>
              ))}
            </div>
          </div>

          {/* 🏢 B2B Section */}
          <div style={{ background: '#f0fdf4', border: '1.5px solid #86efac', borderRadius: 10, padding: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: '#16a34a', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
              🏢 B2B Business Tax (When Buyer Has Tax ID)
            </div>
            <label style={{ fontSize: 10, fontWeight: 700, color: '#166534', display: 'block', marginBottom: 3 }}>B2B TAX NAME</label>
            <input type="text" value={form.b2b_tax_name || ''}
              placeholder={form.tax_name ? `Same (${form.tax_name})` : 'e.g. Corporate Tax, B2B VAT'}
              onChange={e => setForm(f => ({ ...f, b2b_tax_name: e.target.value }))}
              style={{ width: '100%', padding: '6px 8px', border: '1px solid #86efac', borderRadius: 6, fontSize: 12, outline: 'none', marginBottom: 8, background: '#fff', boxSizing: 'border-box' as const }} />

            <label style={{ fontSize: 10, fontWeight: 700, color: '#166534', display: 'block', marginBottom: 3 }}>B2B TAX RATE (%)</label>
            <input type="number" step="0.01" value={form.b2b_tax_rate ?? ''}
              placeholder={form.tax_rate !== undefined ? `Same (${form.tax_rate}%)` : 'e.g. 18'}
              onChange={e => setForm(f => ({ ...f, b2b_tax_rate: e.target.value === '' ? undefined : parseFloat(e.target.value) || 0 }))}
              style={{ width: '100%', padding: '6px 8px', border: '1px solid #86efac', borderRadius: 6, fontSize: 12, fontWeight: 700, color: '#16a34a', outline: 'none', background: '#fff', boxSizing: 'border-box' as const }} />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
              <span style={{ fontSize: 9, color: '#16a34a' }}>Leave blank to inherit B2C</span>
              {form.b2b_tax_rate !== undefined && (
                <button type="button" onClick={() => setForm(f => ({ ...f, b2b_tax_rate: undefined, b2b_tax_name: '' }))}
                  style={{ fontSize: 9, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, textDecoration: 'underline' }}>
                  Reset to B2C
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      <div>
        <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Tax Type</label>
        <button type="button" onClick={() => setForm(f => ({ ...f, tax_inclusive: !f.tax_inclusive }))}
          style={{
            width: '100%', padding: '7px 10px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
            border: `1.5px solid ${form.tax_inclusive ? '#6366f1' : '#e2e8f0'}`,
            background: form.tax_inclusive ? '#eef2ff' : '#fff',
            color: form.tax_inclusive ? '#4f46e5' : '#64748b',
          }}>
          {form.tax_inclusive ? '✓ Price includes tax' : 'Price excludes tax'}
        </button>
        {(() => {
          const scheme = getSchemeById(form.tax_scheme_id);
          const rate = form.tax_rate || 0;
          if (rate <= 0) return null;

          const hasMultiComponent = (scheme?.components && scheme.components.length > 1);
          const scenarios = hasMultiComponent && scheme ? computeTaxScenarios(scheme, form.rate || 0, rate, outletRegion) : null;

          if (scenarios) {
            return (
              <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4, lineHeight: 1.5 }}>
                <div>Same state: <strong style={{ color: '#475569' }}>{formatTaxBreakdown(scenarios.sameState)}</strong></div>
                <div>Different state: <strong style={{ color: '#475569' }}>{formatTaxBreakdown(scenarios.differentState)}</strong></div>
                <div style={{ marginTop: 2, fontStyle: 'italic' }}>Applied automatically per sale — nothing to pick here.</div>
              </div>
            );
          }

          // Flat tax preview (VAT, Sales Tax, single custom tax)
          const taxTitle = form.tax_name || scheme?.components?.[0]?.name || scheme?.label?.replace(/\s*\(.*?\)/, '') || 'Tax';
          const price = form.rate || 0;
          if (price <= 0) return null;

          const taxAmt = form.tax_inclusive
            ? price - (price / (1 + rate / 100))
            : (price * rate) / 100;

          return (
            <div style={{ fontSize: 10, color: '#64748b', marginTop: 4, fontWeight: 600 }}>
              {form.tax_inclusive
                ? `Includes ${currSym}${taxAmt.toFixed(2)} ${taxTitle} (${rate}%)`
                : `+${currSym}${taxAmt.toFixed(2)} ${taxTitle} (${rate}%) → Total: ${currSym}${(price + taxAmt).toFixed(2)}`}
            </div>
          );
        })()}
      </div>

           {/* Opening Stock */}
      <div>
        <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Opening Stock</label>
        <input type="number" value={form.stock_quantity || ''} onChange={e => setForm(f => ({ ...f, stock_quantity: parseInt(e.target.value) || 0 }))}
          style={{ width: '100%', padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
      </div>
      <div>
        <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Low Stock Alert</label>
       <input type="number" value={form.low_stock_threshold ?? ''} onChange={e => setForm(f => ({ ...f, low_stock_threshold: parseInt(e.target.value) || 0 }))}
          style={{ width: '100%', padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
      </div>

      {/* Category */}
      <div>
        <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Category</label>
        <select value={categoryCustom ? '__custom__' : (form.category ?? '')}
          onChange={e => { if (e.target.value === '__custom__') { setCategoryCustom(true); setForm(f => ({ ...f, category: '' })); } else { setCategoryCustom(false); setForm(f => ({ ...f, category: e.target.value })); } }}
          style={{ width: '100%', padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, color: '#111827', background: '#fff', outline: 'none', boxSizing: 'border-box' as const }}>
          <option value="">-- Select Category --</option>
          <optgroup label="Food & Restaurant">
            <option value="starters">Starters</option><option value="main_course">Main Course</option>
            <option value="breads">Breads</option><option value="rice_biryani">Rice & Biryani</option>
            <option value="desserts">Desserts</option><option value="beverages">Beverages</option>
            <option value="snacks">Snacks</option><option value="combos">Combos</option>
          </optgroup>
          <optgroup label="Grocery & Retail">
            <option value="dairy">Dairy</option><option value="bakery">Bakery</option>
            <option value="fruits_vegetables">Fruits & Vegetables</option><option value="grains_pulses">Grains & Pulses</option>
            <option value="spices">Spices & Condiments</option><option value="packaged_food">Packaged Food</option>
            <option value="beverages_retail">Beverages (Retail)</option><option value="cleaning">Cleaning Supplies</option>
            <option value="personal_care">Personal Care</option>
          </optgroup>
          <optgroup label="Clothing & Fashion">
            <option value="mens_wear">Men's Wear</option><option value="womens_wear">Women's Wear</option>
            <option value="kids_wear">Kids' Wear</option><option value="footwear">Footwear</option>
            <option value="accessories">Accessories</option>
          </optgroup>
          <optgroup label="Electronics">
            <option value="mobile_accessories">Mobile Accessories</option><option value="computers">Computers & Laptops</option>
            <option value="appliances">Appliances</option><option value="cables_chargers">Cables & Chargers</option>
          </optgroup>
          <optgroup label="Pharmacy">
            <option value="medicines">Medicines</option><option value="supplements">Supplements</option>
            <option value="medical_devices">Medical Devices</option><option value="cosmetics">Cosmetics</option>
          </optgroup>
          <optgroup label="Services">
            <option value="consulting">Consulting</option><option value="repair">Repair & Maintenance</option>
            <option value="delivery">Delivery</option><option value="subscription">Subscription</option>
            <option value="other_service">Other Service</option>
          </optgroup>
          <optgroup label="Other">
            <option value="stationery">Stationery</option><option value="hardware">Hardware & Tools</option>
            <option value="furniture">Furniture</option><option value="other">Other</option>
          </optgroup>
          <option value="__custom__">✏️ Type custom...</option>
        </select>
        {categoryCustom && (
          <input type="text" autoFocus placeholder="Type your custom category..." value={form.category}
            onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
            style={{ width: '100%', marginTop: 6, padding: '7px 10px', border: '1px solid #6366f1', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box' as const }} />
        )}
      </div>

      {/* Unit */}
      <div>
        <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Unit</label>
        <select value={unitCustom ? '__custom__' : (form.unit ?? 'pcs')}
          onChange={e => { if (e.target.value === '__custom__') { setUnitCustom(true); setForm(f => ({ ...f, unit: '' })); } else { setUnitCustom(false); setForm(f => ({ ...f, unit: e.target.value })); } }}
          style={{ width: '100%', padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, color: '#111827', background: '#fff', outline: 'none', boxSizing: 'border-box' as const }}>
          <optgroup label="Count">
            <option value="pcs">pcs — Pieces</option><option value="unit">unit</option><option value="dozen">dozen</option>
            <option value="pair">pair</option><option value="set">set</option><option value="box">box</option>
            <option value="pack">pack</option><option value="carton">carton</option><option value="roll">roll</option>
            <option value="bundle">bundle</option><option value="bag">bag</option><option value="bottle">bottle</option>
            <option value="can">can</option><option value="jar">jar</option><option value="sachet">sachet</option>
            <option value="strip">strip — (pharmacy)</option><option value="tablet">tablet</option>
            <option value="capsule">capsule</option><option value="vial">vial</option><option value="ampoule">ampoule</option>
          </optgroup>
          <optgroup label="Weight">
            <option value="kg">kg</option><option value="g">g — grams</option><option value="mg">mg — milligrams</option>
            <option value="lb">lb — pounds</option><option value="oz">oz — ounces</option>
            <option value="ton">ton</option><option value="quintal">quintal</option>
          </optgroup>
          <optgroup label="Volume">
            <option value="l">L — litres</option><option value="ml">ml — millilitres</option>
            <option value="cl">cl — centilitres</option><option value="gal">gal — gallon</option><option value="fl oz">fl oz</option>
          </optgroup>
          <optgroup label="Length / Area">
            <option value="m">m — metres</option><option value="cm">cm</option><option value="mm">mm</option>
            <option value="ft">ft — feet</option><option value="inch">inch</option><option value="yard">yard</option>
            <option value="sqft">sqft</option><option value="sqm">sqm</option>
          </optgroup>
          <optgroup label="Food / Restaurant">
            <option value="plate">plate</option><option value="bowl">bowl</option><option value="serving">serving</option>
            <option value="portion">portion</option><option value="glass">glass</option><option value="cup">cup</option>
            <option value="half">half</option><option value="full">full</option>
          </optgroup>
          <optgroup label="Other">
            <option value="hr">hr — hours</option><option value="day">day</option><option value="month">month</option>
            <option value="visit">visit</option><option value="job">job</option><option value="lot">lot</option>
          </optgroup>
          <option value="__custom__">✏️ Type custom...</option>
        </select>
        {unitCustom && (
          <input type="text" autoFocus placeholder="Type your custom unit..." value={form.unit}
            onChange={e => setForm(f => ({ ...f, unit: e.target.value }))}
            style={{ width: '100%', marginTop: 6, padding: '7px 10px', border: '1px solid #6366f1', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box' as const }} />
        )}
      </div>
    </div>

    {customKeys.length > 0 && (
      <div style={{ marginTop: 12 }}>
        <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>Custom Fields</label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
          {customKeys.map(k => (
            <div key={k}>
              <label style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', display: 'block', marginBottom: 4 }}>{k}</label>
              <input type="text" value={customFieldValues[k] || ''}
                onChange={e => setCustomFieldValues(v => ({ ...v, [k]: e.target.value }))}
                placeholder={k}
                style={{ width: '100%', padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
            </div>
          ))}
        </div>
      </div>
    )}

    <div style={{ marginTop: 12, padding: '10px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, fontSize: 12, color: '#92400e' }}>
      💡 Click <strong>⚡ EAN-13</strong> to generate a real scannable barcode, or leave blank for a text code.
    </div>

    <div style={{ marginTop: 12 }}>
      <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Description</label>
      <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2}
        style={{ width: '100%', padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
    </div>

   

    {/* Action buttons */}
    <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
      <button onClick={() => {
        if (!form.name) { setError('Product name is required'); return; }
        setError('');
        const entry = {
          ...form,
          barcode: form.barcode || generateBarcode(),
          custom_fields: {
            ...customFieldValues,
            ...customFields.reduce((acc, cf) => { if (cf.label.trim()) acc[cf.label.trim()] = cf.value; return acc; }, {} as Record<string, string>),
          },
        };
        setProductQueue(q => [...q, { ...entry, tax_scheme_id: entry.tax_scheme_id || defaultScheme.id }]);
        setForm(empty(defaultScheme.id));
        setCustomFields([]);
        setNewFieldLabel('');
        setCustomFieldValues({});
        setCategoryCustom(false);
        setUnitCustom(false);
      }}
        style={{ padding: '8px 20px', background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
        + Add Another
      </button>

      <button onClick={handleBulkSave} disabled={saving || (productQueue.length === 0 && !form.name)}
        style={{ padding: '8px 20px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
        {saving ? 'Saving...' : `Save ${productQueue.length > 0 ? `All ${productQueue.length + (form.name ? 1 : 0)}` : 'Product'}`}
      </button>

      <button onClick={() => { setShowAdd(false); setForm(empty()); setError(''); setCategoryCustom(false); setUnitCustom(false); setCustomFields([]); setNewFieldLabel(''); setCustomFieldValues({}); setProductQueue([]); }}
        style={{ padding: '8px 16px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
        Cancel
      </button>
    </div>
  </div>
  </div>
)}

      {selectedIds.size > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 16px', borderRadius: 10, marginBottom: 12,
          background: '#eff6ff', border: '1px solid #bfdbfe', fontSize: 13, fontWeight: 700, color: '#1d4ed8',
        }}>
          <span>{selectedIds.size} selected</span>
          <div style={{ display: 'flex', gap: 8 }}>
            {perms.canRemoveProduct && (
              <button onClick={handleBulkDelete}
                style={{ padding: '7px 14px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                🗑️ Delete Selected
              </button>
            )}
            <button onClick={() => setSelectedIds(new Set())}
              style={{ padding: '7px 14px', background: '#fff', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 8, fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
              Clear
            </button>
          </div>
        </div>
      )}

      {showScanner && (
        <QuickScannerModal
          onAddItem={handleScanAddItem}
          onClose={() => setShowScanner(false)}
          context="catalog"
        />
      )}

      {/* Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8', fontSize: 14 }}>Loading products...</div>
      ) : filtered.length === 0 ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '60px 20px',
          textAlign: 'center',
          width: '100%',
        }}>
          <Package size={44} style={{ color: '#334155', marginBottom: 12 }} />
          <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#0f172a' }}>No products yet</p>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: '#64748b' }}>Import an Excel sheet, add manually, or scan a barcode</p>
        </div>
      ) : (
        <div className="excel-table-wrap" style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, height: 'calc(100vh - 300px)', minHeight: 320, overflow: 'auto', position: 'relative' as const }}>
         <div className="excel-header" style={{ display: 'grid', gridTemplateColumns: `44px 300px 1fr 160px 140px 120px 150px 140px 100px 90px 80px ${customKeys.map(() => '150px').join(' ')} 90px 90px`, gap: 0, background: '#f8fafc', borderBottom: '1px solid #cbd5e1', padding: '14px 0', fontSize: 13, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', minWidth: tableMinWidth, position: 'sticky' as const, top: 0, zIndex: 8 }}>
  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <input
      type="checkbox"
      checked={filtered.length > 0 && selectedIds.size === filtered.length}
      onChange={toggleSelectAll}
      style={{
        width: 17,
        height: 17,
        accentColor: '#3b82f6',
        cursor: 'pointer',
        flexShrink: 0,
      }}
      title="Select all products"
    />
  </span>
  <span>Barcode</span><span>Name</span><span>Brand</span><span>Category</span><span>Rate</span><span>B2C Tax</span><span>B2B Tax</span><span>Unit</span><span>Stock</span><span>Min</span>
{customKeys.map(k => (
  <span key={k} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
    {k}
    <button
      title="Hide column"
      onClick={() => setHiddenKeys(h => [...h, k])}
      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 0, fontSize: 10 }}>
      👁
    </button>
    {perms.canRemoveProduct && (
    <button
      title="Delete this field from all products"
      onClick={async () => {
        if (!confirm(`Delete "${k}" from ALL products? This cannot be undone.`)) return;
        const jwt = localStorage.getItem('cloudairambo_jwt') || '';
        await Promise.all(products.map(p => {
          if (!p.custom_fields?.[k]) return Promise.resolve();
          const updated = { ...p.custom_fields };
          delete updated[k];
          return fetch(`${BACKEND}/products/${p.id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
            },
            body: JSON.stringify({ custom_fields: updated }),
          });
        }));
        setProducts(ps => ps.map(p => {
          const updated = { ...(p.custom_fields || {}) };
          delete updated[k];
          return { ...p, custom_fields: updated };
        }));
      }}
      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 0, fontSize: 10 }}>
      <X size={11} />
    </button>
    )}
  </span>
))}
  <span></span><span></span>
</div>

          {filtered.map(p => {
            const isSelected = selectedIds.has(p.id);
            return (
            <div 
              key={p.id} 
              className={`excel-row ${isSelected ? 'row-selected' : ''}`} 
              style={{ 
                display: 'grid', 
                gridTemplateColumns: `44px 300px 1fr 160px 140px 120px 150px 140px 100px 90px 80px ${customKeys.map(() => '150px').join(' ')} 90px 90px`, 
                gap: 0, 
                borderBottom: '1px solid #e2e8f0', 
                fontSize: 15, 
                minWidth: tableMinWidth,
                background: isSelected ? '#eff6ff' : undefined,
              }}
            >
              {editingId === p.id && editForm ? (
                <>
                  {/* Empty cell to keep the 44px checkbox column aligned */}
                  <span />
                  <input value={editForm.barcode} onChange={e => setEditForm(f => f ? { ...f, barcode: e.target.value } : f)}
                    style={{ padding: '4px 6px', border: '1px solid #3b82f6', borderRadius: 6, fontSize: 12, outline: 'none' }} />
                  <input value={editForm.name} onChange={e => setEditForm(f => f ? { ...f, name: e.target.value } : f)}
                    style={{ padding: '4px 6px', border: '1px solid #3b82f6', borderRadius: 6, fontSize: 12, outline: 'none' }} />
                  <input value={editForm.brand || ''} onChange={e => setEditForm(f => f ? { ...f, brand: e.target.value } : f)}
                    placeholder="Brand" style={{ padding: '4px 6px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 11, outline: 'none', color: '#64748b' }} />
                  <input value={editForm.category || ''} onChange={e => setEditForm(f => f ? { ...f, category: e.target.value } : f)}
                    style={{ padding: '4px 6px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, outline: 'none' }} />
                  <input type="number" value={editForm.rate} onChange={e => setEditForm(f => f ? { ...f, rate: parseFloat(e.target.value) || 0 } : f)}
                    style={{ padding: '4px 6px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, outline: 'none' }} />
                  
                  {/* Inline edit: B2C Tax */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%', border: '1px solid #e2e8f0', borderRadius: 7, padding: 3, background: '#f8fafc', boxSizing: 'border-box' }}>
                    <div style={{ position: 'relative', width: '100%' }}>
                      <input type="number" value={editForm.tax_rate || ''} placeholder="0"
                        onChange={e => setEditForm(f => f ? { ...f, tax_rate: parseFloat(e.target.value) || 0 } : f)}
                        style={{ width: '100%', padding: '4px 18px 4px 6px', border: '1px solid #e2e8f0', borderRadius: 5, fontSize: 12, fontWeight: 600, color: '#0f172a', outline: 'none', boxSizing: 'border-box', background: '#fff' }} />
                      <span style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', fontSize: 10, fontWeight: 600, color: '#94a3b8', pointerEvents: 'none' }}>%</span>
                    </div>
                    <button type="button" onClick={() => setEditForm(f => f ? { ...f, tax_inclusive: !f.tax_inclusive } : f)}
                      style={{
                        width: '100%', fontSize: 9, fontWeight: 700, letterSpacing: '0.03em', textTransform: 'uppercase' as const,
                        padding: '3px 4px', borderRadius: 5, cursor: 'pointer', boxSizing: 'border-box',
                        border: `1px solid ${editForm.tax_inclusive ? '#a855f7' : '#e2e8f0'}`,
                        background: editForm.tax_inclusive ? '#f5f3ff' : '#fff',
                        color: editForm.tax_inclusive ? '#7e22ce' : '#94a3b8',
                      }}>
                      {editForm.tax_inclusive ? 'INCL.' : 'EXCL.'}
                    </button>
                  </div>

                  {/* Inline edit: B2B Tax */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%', border: '1.5px solid #86efac', borderRadius: 7, padding: 3, background: '#f0fdf4', boxSizing: 'border-box' }}>
                    <div style={{ position: 'relative', width: '100%' }}>
                      <input type="number" value={editForm.b2b_tax_rate ?? ''} placeholder={String(editForm.tax_rate ?? 0)}
                        onChange={e => setEditForm(f => f ? { ...f, b2b_tax_rate: e.target.value === '' ? undefined : (parseFloat(e.target.value) || 0) } : f)}
                        style={{ width: '100%', padding: '4px 18px 4px 6px', border: '1px solid #86efac', borderRadius: 5, fontSize: 12, fontWeight: 700, color: '#16a34a', outline: 'none', boxSizing: 'border-box', background: '#fff' }} />
                      <span style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', fontSize: 10, fontWeight: 600, color: '#16a34a', pointerEvents: 'none' }}>%</span>
                    </div>
                    <span style={{ fontSize: 9, color: '#16a34a', textAlign: 'center', fontWeight: 600 }}>B2B Rate</span>
                  </div>

                  <input value={editForm.unit || ''} onChange={e => setEditForm(f => f ? { ...f, unit: e.target.value } : f)}
  style={{ padding: '4px 6px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, outline: 'none' }} />
<input type="number" value={editForm.stock_quantity ?? 0} onChange={e => setEditForm(f => f ? { ...f, stock_quantity: parseInt(e.target.value) || 0 } : f)}
  style={{ padding: '4px 6px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, outline: 'none' }} />
<input type="number" value={editForm.low_stock_threshold ?? 5} onChange={e => setEditForm(f => f ? { ...f, low_stock_threshold: parseInt(e.target.value) || 0 } : f)}
  placeholder="Min" style={{ padding: '4px 6px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, outline: 'none' }} />
{customKeys.map(k => (
  <input key={k}
    value={editForm.custom_fields?.[k] || ''}
    placeholder={k}
    onChange={e => setEditForm(f => f ? { ...f, custom_fields: { ...f.custom_fields, [k]: e.target.value } } : f)}
    style={{ padding: '4px 6px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, outline: 'none' }} />
))}
<button onClick={() => handleUpdate(p.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#22c55e' }}><Check size={16} /></button>
                  <button onClick={() => setEditingId(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}><X size={16} /></button>
                </>
              ) : (
                <>
                  <span 
                    onClick={() => toggleSelect(p.id)}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      cursor: 'pointer',
                      width: '100%',
                      height: '100%',
                      padding: 0,
                    }}
                    title="Select row"
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelect(p.id)}
                      onClick={e => e.stopPropagation()}
                      style={{
                        width: 18,
                        height: 18,
                        accentColor: '#2563eb',
                        cursor: 'pointer',
                        flexShrink: 0,
                      }}
                    />
                  </span>
                  <span title={p.barcode} style={{ fontFamily: 'monospace', fontSize: 11, color: '#475569', background: '#f8fafc', padding: '2px 6px', borderRadius: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.barcode}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 600, color: '#0f172a' }}>{p.name}</span>
                    {(p as any).outlet_id ? (
                      <span style={{ fontSize: 9, fontWeight: 700, background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a', padding: '1px 6px', borderRadius: 99, textTransform: 'uppercase' as const, letterSpacing: '0.04em' }}>
                        🏪 Outlet
                      </span>
                    ) : (
                      <span style={{ fontSize: 9, fontWeight: 700, background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0', padding: '1px 6px', borderRadius: 99, textTransform: 'uppercase' as const, letterSpacing: '0.04em' }}>
                        🌐 Shared
                      </span>
                    )}
                  </div>
                  <span style={{ fontSize: 11, color: '#94a3b8' }}>{p.brand || '—'}</span>
                  <span style={{ fontSize: 11, color: '#64748b' }}>{p.category || '—'}</span>
                  <span style={{ fontWeight: 700, color: '#0f172a' }}>{p.rate > 0 ? `${currSym}${p.rate.toFixed(2)}` : '—'}</span>

                  {/* COLUMN 1: B2C TAX (Rate + INCL./EXCL. Pill + Tax Name / Split) */}
                  <span style={{ display: 'flex', alignItems: 'center' }}>
                    {(() => {
                      const scheme = getSchemeById(p.tax_scheme_id) || defaultScheme;
                      const hasManualTax = (p.tax_rate || 0) > 0;

                      if (scheme?.kind === 'none' && !hasManualTax) {
                        return <span style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>0%</span>;
                      }

                      const hasMultiComponent = (scheme?.components && scheme.components.length > 1);
                      const scenarios = hasMultiComponent ? computeTaxScenarios(scheme!, p.rate || 0, p.tax_rate || 0, outletRegion) : null;
                      const inlineSplit = scenarios
                        ? formatTaxBreakdown(scenarios.sameState)
                        : hasMultiComponent
                          ? formatTaxBreakdown(computeTaxBreakdown(scheme!, p.rate || 0, {}, p.tax_rate || 0))
                          : null;

                      // Display custom tax name or fallback to store custom tax scheme name
                      const flatTaxLabel = p.tax_name || customTaxScheme?.name || scheme.components?.[0]?.name || scheme.label.replace(/\s*\(.*?\)/, '');

                      return (
                        <button type="button"
                          onClick={e => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            setTaxPopover(prev => prev?.productId === p.id && (prev as any).taxType === 'b2c' ? null : { productId: p.id, x: rect.left, y: rect.bottom + 4, taxType: 'b2c' } as any);
                          }}
                          style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left' as const }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>
                              {p.tax_rate || 0}%
                            </span>
                            <span style={{
                              fontSize: 9, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase' as const,
                              padding: '1px 6px', borderRadius: 6,
                              border: p.tax_inclusive ? '1px solid #c084fc' : '1px solid #e2e8f0',
                              background: p.tax_inclusive ? '#f5f3ff' : '#f8fafc',
                              color: p.tax_inclusive ? '#7e22ce' : '#64748b',
                            }}>
                              {p.tax_inclusive ? 'INCL.' : 'EXCL.'}
                            </span>
                          </span>
                          {inlineSplit ? (
                            <span style={{ fontSize: 9, color: '#94a3b8', whiteSpace: 'nowrap' }}>{inlineSplit}</span>
                          ) : (
                            <span style={{ fontSize: 9, color: '#64748b', whiteSpace: 'nowrap', fontWeight: 600 }}>{flatTaxLabel}</span>
                          )}
                        </button>
                      );
                    })()}
                  </span>

                  {/* COLUMN 2: B2B TAX */}
                  <span style={{ display: 'flex', alignItems: 'center' }}>
                    {(() => {
                      const scheme = getSchemeById(p.tax_scheme_id) || defaultScheme;
                      const hasCustomB2B = p.b2b_tax_rate !== undefined && p.b2b_tax_rate !== null && String(p.b2b_tax_rate).trim() !== '' && Number(p.b2b_tax_rate) !== Number(p.tax_rate || 0);
                      const effectiveB2B = (p.b2b_tax_rate !== undefined && p.b2b_tax_rate !== null && String(p.b2b_tax_rate).trim() !== '')
                        ? Number(p.b2b_tax_rate)
                        : (p.tax_rate || 0);

                      // Self-contained labels for B2B column
                      const b2cLabel = p.tax_name || customTaxScheme?.name || scheme.components?.[0]?.name || scheme.label.replace(/\s*\(.*?\)/, '');
                      const b2bLabel = hasCustomB2B ? (p.b2b_tax_name || `${b2cLabel} B2B`) : b2cLabel;

                      return (
                        <button type="button"
                          onClick={e => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            setTaxPopover(prev => prev?.productId === p.id && (prev as any).taxType === 'b2b' ? null : { productId: p.id, x: rect.left, y: rect.bottom + 4, taxType: 'b2b' } as any);
                          }}
                          style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left' as const }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontSize: 12, fontWeight: 700, color: '#16a34a' }}>
                              {effectiveB2B}%
                            </span>
                            <span style={{
                              fontSize: 9, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase' as const,
                              padding: '1px 6px', borderRadius: 6,
                              border: hasCustomB2B ? '1.5px solid #86efac' : '1px solid #bbf7d0',
                              background: hasCustomB2B ? '#dcfce7' : '#f0fdf4',
                              color: hasCustomB2B ? '#15803d' : '#16a34a',
                            }}>
                              {hasCustomB2B ? 'CUSTOM' : 'ITC'}
                            </span>
                          </span>
                          <span style={{ fontSize: 9, color: hasCustomB2B ? '#15803d' : '#64748b', whiteSpace: 'nowrap', fontWeight: 600 }}>
                            {b2bLabel}
                          </span>
                        </button>
                      );
                    })()}
                  </span>

                  <span style={{ fontSize: 11, color: '#64748b' }}>{p.unit || '—'}</span>
<span style={{
  fontSize: 12, fontWeight: 800,
  color: (p.stock_quantity ?? 0) === 0 ? '#ef4444' : ((p.stock_quantity ?? 0) <= (p.low_stock_threshold ?? 5)) ? '#f59e0b' : '#16a34a'
}}>
  {p.stock_quantity ?? 0}
</span>
<span style={{ fontSize: 11, color: '#94a3b8' }}>{p.low_stock_threshold ?? 5}</span>
{customKeys.map(k => (
  <span key={k} style={{ fontSize: 11, color: '#64748b' }}>{p.custom_fields?.[k] || '—'}</span>
))}
{perms.canEditProduct
                    ? <button onClick={() => { setEditingId(p.id); setEditForm({ ...p, custom_fields: p.custom_fields || {} }); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3b82f6' }}><Edit2 size={14} /></button>
                    : <span />}
                  {perms.canRemoveProduct
                    ? <button onClick={() => handleDelete(p.id)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444' }}><Trash2 size={14} /></button>
                    : <span />}
                </>
              )}
            </div>
          );
        })}
        </div>
     
      )}

      {/* Tax breakdown popover — showing ONLY B2C or ONLY B2B */}
      {taxPopover && (() => {
        const product = products.find(pr => pr.id === taxPopover.productId);
        const scheme = product ? (getSchemeById(product.tax_scheme_id) || defaultScheme) : undefined;
        if (!product || !scheme) return null;

        const isB2BClicked = (taxPopover as any).taxType === 'b2b';
        const b2cRate = product.tax_rate || 0;
        const hasCustomB2B = product.b2b_tax_rate !== undefined && product.b2b_tax_rate !== null && String(product.b2b_tax_rate).trim() !== '' && Number(product.b2b_tax_rate) !== Number(b2cRate);
        const b2bRate = (product.b2b_tax_rate !== undefined && product.b2b_tax_rate !== null && String(product.b2b_tax_rate).trim() !== '')
          ? Number(product.b2b_tax_rate)
          : b2cRate;

        const activeRate = isB2BClicked ? b2bRate : b2cRate;
        const defaultTaxName = scheme.components?.[0]?.name || scheme.label.replace(/\s*\(.*?\)/, '') || 'Tax';
        const b2cTaxName = product.tax_name || defaultTaxName;
        // When B2B is standard ITC, its name matches B2C (e.g. PPN); only custom B2B uses a custom name
        const b2bTaxName = hasCustomB2B ? (product.b2b_tax_name || `${b2cTaxName} B2B`) : b2cTaxName;
        const activeTaxName = isB2BClicked ? b2bTaxName : b2cTaxName;
        const rawPrice = product.rate || 0;

        // B2C Math
        const b2cBase = product.tax_inclusive ? (b2cRate > 0 ? rawPrice / (1 + b2cRate / 100) : rawPrice) : rawPrice;
        const b2cTax = product.tax_inclusive ? (rawPrice - b2cBase) : (rawPrice * b2cRate / 100);
        const b2cTotal = product.tax_inclusive ? rawPrice : (rawPrice + b2cTax);

        // B2B Math
        const b2bBase = product.tax_inclusive ? (b2bRate > 0 ? rawPrice / (1 + b2bRate / 100) : rawPrice) : rawPrice;
        const b2bTax = product.tax_inclusive ? (rawPrice - b2bBase) : (rawPrice * b2bRate / 100);
        const b2bTotal = b2bBase + b2bTax;

        const hasMultiComponent = (scheme.components && scheme.components.length > 1);
        const scenarios = hasMultiComponent ? computeTaxScenarios(scheme, isB2BClicked ? b2bBase : b2cBase, activeRate, outletRegion) : null;

        return (
          <>
            <div onClick={() => setTaxPopover(null)} style={{ position: 'fixed', inset: 0, zIndex: 199 }} />
            <div style={{
              position: 'fixed', left: Math.min(taxPopover.x, window.innerWidth - 320), top: taxPopover.y, zIndex: 200,
              background: '#fff', border: isB2BClicked ? '1.5px solid #86efac' : '1px solid #e2e8f0', borderRadius: 14,
              boxShadow: '0 16px 40px rgba(15,23,42,0.22)', padding: '16px', width: 310, boxSizing: 'border-box' as const,
            }}>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: isB2BClicked ? '#166534' : '#1e1b4b' }}>
                  {isB2BClicked ? '🏢 ' : '👤 '}{activeTaxName}
                </div>
                <span style={{ fontSize: 11, fontWeight: 800, color: isB2BClicked ? '#15803d' : '#4f46e5', background: isB2BClicked ? '#dcfce7' : '#eef2ff', padding: '2px 8px', borderRadius: 6 }}>
                  {activeRate}% {product.tax_inclusive ? 'INCL' : 'EXCL'}
                </span>
              </div>

              {/* ONLY B2C VIEW */}
              {!isB2BClicked && (
                <div style={{ background: '#f8fafc', borderRadius: 10, padding: '12px', border: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: '#6366f1', textTransform: 'uppercase', marginBottom: 8 }}>
                    👤 Walk-in Retail Customer
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#64748b', marginBottom: 4 }}>
                    <span>Base Net Price:</span>
                    <strong style={{ color: '#0f172a' }}>{currSym}{b2cBase.toFixed(2)}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#64748b', marginBottom: 6 }}>
                    <span>{b2cTaxName} ({b2cRate}%):</span>
                    <strong style={{ color: '#4f46e5' }}>+{currSym}{b2cTax.toFixed(2)}</strong>
                  </div>
                  <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 6, display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 800 }}>
                    <span>Total Pay:</span>
                    <span style={{ color: '#0f172a' }}>{currSym}{b2cTotal.toFixed(2)}</span>
                  </div>
                  <div style={{ fontSize: 9, color: '#94a3b8', marginTop: 6, fontStyle: 'italic' }}>
                    {product.tax_inclusive ? '✓ Tax is already included in item price' : '+ Tax is added on top at checkout'}
                  </div>
                </div>
              )}

              {/* ONLY B2B VIEW */}
              {isB2BClicked && (
                <div style={{ background: '#f0fdf4', borderRadius: 10, padding: '12px', border: '1px solid #bbf7d0' }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: '#16a34a', textTransform: 'uppercase', marginBottom: 8 }}>
                    🏢 Business Buyer (With Tax ID)
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#166534', marginBottom: 4 }}>
                    <span>Base Net Price:</span>
                    <strong style={{ color: '#0f172a' }}>{currSym}{b2bBase.toFixed(2)}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#166534', marginBottom: 6 }}>
                    <span>{b2bTaxName} ({b2bRate}%):</span>
                    <strong style={{ color: '#15803d' }}>+{currSym}{b2bTax.toFixed(2)}</strong>
                  </div>
                  <div style={{ borderTop: '1px solid #bbf7d0', paddingTop: 6, display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 800, marginBottom: 8 }}>
                    <span>Invoice Total:</span>
                    <span style={{ color: '#0f172a' }}>{currSym}{b2bTotal.toFixed(2)}</span>
                  </div>
                  <div style={{ background: '#dcfce7', borderRadius: 7, padding: '7px 9px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: '#15803d' }}>Tax Credit (ITC):</span>
                    <span style={{ fontSize: 13, fontWeight: 900, color: '#15803d' }}>+{currSym}{b2bTax.toFixed(2)}</span>
                  </div>
                  <div style={{ fontSize: 9, color: '#15803d', marginTop: 6 }}>
                    ✓ Buyer claims 100% tax credit back with Tax ID
                  </div>
                </div>
              )}

              {/* Optional Component Split if region-based */}
              {scenarios && (
                <div style={{ marginTop: 10, background: '#f8fafc', borderRadius: 8, padding: '8px', fontSize: 11 }}>
                  <div style={{ fontSize: 9, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>
                    Tax Component Split
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: '#334155' }}>
                    <span>Same State:</span>
                    <strong style={{ color: '#0f172a' }}>{formatTaxBreakdown(scenarios.sameState)}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: '#334155', marginTop: 2 }}>
                    <span>Different State:</span>
                    <strong style={{ color: '#0f172a' }}>{formatTaxBreakdown(scenarios.differentState)}</strong>
                  </div>
                </div>
              )}

              <div style={{ fontSize: 9, color: '#94a3b8', fontStyle: 'italic', marginTop: 10, textAlign: 'center' }}>
                Decided automatically on invoice when customer enters Tax ID.
              </div>
            </div>
          </>
        );
      })()}
    </div>
  );
}
